import React from 'react'
import { createPortal } from 'react-dom'
import AgoraRTC from 'agora-rtc-react'
import AgoraRTM from 'agora-rtm-sdk'
import { FormControl, MenuItem, Popover, Select } from '@mui/material'
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded'
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded'
import MicRoundedIcon from '@mui/icons-material/MicRounded'
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded'
import ScreenShareRoundedIcon from '@mui/icons-material/ScreenShareRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import StopScreenShareRoundedIcon from '@mui/icons-material/StopScreenShareRounded'
import VideocamOffRoundedIcon from '@mui/icons-material/VideocamOffRounded'
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded'
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {
  AGORA_REMOTE_PARTICIPANT_EVENT_TYPES,
  reduceAgoraRemoteParticipants,
} from './agoraRemoteParticipantState'
import { resolveAgoraPresenceRetireOptions } from './agoraPresenceRetireState'
import { normalizeResolvedVideoCallQuality } from '../../../routing/videoCallQualityContract'
import {
  MEDIA_ERROR_REASONS,
  classifyMediaError,
  isRetryableMediaError,
  isMacPlatform,
  isStandaloneDisplayMode,
  buildDeviceErrorNotice,
} from './mediaDeviceErrors'

const AGORA_LAYOUTS = Object.freeze({
  grid: 'grid',
  pin: 'pin',
})

const TOGGLE_STATES = Object.freeze({
  disabled: 0,
  enabled: 1,
  disabling: 2,
  enabling: 3,
})

const REMOTE_TRACK_STATES = Object.freeze({
  yes: 0,
  subbed: 1,
  no: 2,
})

const REMOTE_MUTING_DEVICES = Object.freeze({
  camera: 'camera',
  microphone: 'microphone',
})

const RTM_MESSAGE_TYPES = Object.freeze({
  muteRequest: 'mute-request',
})

const RTM_CHANNEL_TYPE = 'MESSAGE'

// Serializes RTM login for a given (appId, uid) behind any in-flight logout of the SAME uid.
// On unmount->remount (e.g. under MentorLayout) the RTM effect's cleanup fires an async logout
// that React does not await, so the remount would log the same uid in again mid-logout -> Agora
// rejects it as a duplicate (SAME_UID_LOGIN / LOGIN_TOO_FREQUENT, surfaced as -10023). Keyed by
// uid, so distinct users never block each other. Entries self-clear when the logout settles.
const pendingRtmLogoutByKey = new Map()
const buildRtmBarrierKey = (appId, uid) => `${appId}::${uid}`
const RTM_LOGOUT_BARRIER_TIMEOUT_MS = 4000
const rtmBarrierDelay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

function isDuplicateRtmLoginError(error) {
  const haystack = `${error?.reason || ''} ${error?.name || ''} ${error?.message || ''}`.toLowerCase()
  return (
    error?.code === 27 || // RTMErrorCode.SAME_UID_LOGIN (agora-rtm-sdk 2.x)
    haystack.includes('same_uid_login') ||
    haystack.includes('same uid') ||
    haystack.includes('too_frequent') ||
    haystack.includes('too frequent') ||
    haystack.includes('already login') ||
    haystack.includes('already logged')
  )
}

// Acquiring a local mic/camera track can fail transiently on macOS Chrome (and Chrome PWAs):
// getUserMedia throws NotReadableError / AbortError when the device is momentarily held by the
// OS or another app/tab, or when a stale saved device id is no longer valid. We retry a couple
// of times, and on the final attempt drop the explicit device id so the OS default is used.
const MEDIA_ACQUIRE_MAX_ATTEMPTS = 3
const MEDIA_ACQUIRE_RETRY_DELAY_MS = 500
const IS_MAC_PLATFORM = isMacPlatform()
const IS_STANDALONE_DISPLAY = isStandaloneDisplayMode()

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

const PARTICIPANT_ROLE_LABELS = Object.freeze({
  mentor: 'Mentor',
  patient: 'Patient',
  participant: 'Participant',
  screen: 'Screen',
  'chamber-room': 'Room',
  panelist: 'Panelist',
  auditor: 'Observer',
  observer: 'Observer',
  doctor: 'Doctor',
  user: 'Participant',
})

function noop() {}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeBoolean(value, fallbackValue) {
  return typeof value === 'boolean' ? value : fallbackValue
}

function buildAgoraEncoderConfig(videoQuality) {
  const publishProfile = normalizeResolvedVideoCallQuality(videoQuality)?.publishProfile

  if (!publishProfile) {
    return null
  }

  return {
    width: publishProfile.width,
    height: publishProfile.height,
    frameRate: publishProfile.frameRate,
    bitrateMax: publishProfile.bitrateKbps,
  }
}

function buildAgoraLowStreamParameter(videoQuality) {
  const lowStreamProfile = normalizeResolvedVideoCallQuality(videoQuality)?.lowStreamProfile

  if (!lowStreamProfile) {
    return null
  }

  return {
    width: lowStreamProfile.width,
    height: lowStreamProfile.height,
    framerate: lowStreamProfile.frameRate,
    bitrate: lowStreamProfile.bitrateKbps,
  }
}

function getAgoraRemoteStreamType(videoQuality) {
  return normalizeResolvedVideoCallQuality(videoQuality)?.preferHighQualityRemote === false ? 1 : 0
}

function normalizeLayout(layout) {
  if (layout === 0 || layout === '0' || layout === 'grid') {
    return AGORA_LAYOUTS.grid
  }

  if (layout === 1 || layout === '1' || layout === 'pin' || layout === 'pinned') {
    return AGORA_LAYOUTS.pin
  }

  return AGORA_LAYOUTS.pin
}

function normalizeRole(role) {
  return role === 'audience' ? 'audience' : 'host'
}

function normalizeUid(value, fallbackValue = null) {
  if (value === null || value === undefined || value === '') {
    return fallbackValue
  }

  const numericUid = Number(value)

  if (!Number.isFinite(numericUid)) {
    return fallbackValue
  }

  return numericUid
}

function isIgnoredRemoteRtcUid(uid, localUid) {
  const normalizedUid = normalizeUid(uid, null)
  const normalizedLocalUid = normalizeUid(localUid, null)

  if (normalizedUid === null || normalizedUid === 0) {
    return true
  }

  if (normalizedLocalUid !== null && normalizedUid === normalizedLocalUid) {
    return true
  }

  return false
}

function stringifyUid(value, fallbackValue = '') {
  if (value === null || value === undefined) {
    return fallbackValue
  }

  return String(value)
}

function normalizeParticipantRole(role) {
  if (typeof role !== 'string') {
    return ''
  }

  return role.trim().toLowerCase()
}

function getParticipantRoleLabel(role) {
  const normalizedRole = normalizeParticipantRole(role)

  if (!normalizedRole) {
    return ''
  }

  return PARTICIPANT_ROLE_LABELS[normalizedRole] || role
}

function getAllUidKeys(uid) {
  const keys = new Set()

  if (uid === null || uid === undefined) {
    return []
  }

  keys.add(uid)
  keys.add(String(uid))

  const numericUid = Number(uid)
  if (Number.isFinite(numericUid)) {
    keys.add(numericUid)
    keys.add(String(numericUid))
  }

  return Array.from(keys)
}

function resolveUsernameForUid(usernameMap, uid) {
  if (!isPlainObject(usernameMap)) {
    return undefined
  }

  for (const key of getAllUidKeys(uid)) {
    if (usernameMap[key] !== undefined) {
      return usernameMap[key]
    }
  }

  return undefined
}

function getDisplayNameFromUsername(username) {
  if (typeof username !== 'string') {
    return ''
  }

  const trimmedUsername = username.trim()
  if (!trimmedUsername) {
    return ''
  }

  try {
    const parsedUsername = JSON.parse(trimmedUsername)
    if (
      parsedUsername &&
      typeof parsedUsername === 'object' &&
      !Array.isArray(parsedUsername) &&
      typeof parsedUsername.name === 'string' &&
      parsedUsername.name.trim()
    ) {
      return parsedUsername.name.trim()
    }
  } catch (error) {
    const nameMatch = trimmedUsername.match(/"name"\s*:\s*"([^"]+)"/)
    if (nameMatch?.[1]) {
      return nameMatch[1].trim()
    }
  }

  return trimmedUsername
}

function parseUsernameMetadata(username) {
  if (typeof username !== 'string') {
    return {
      rawUsername: '',
      displayName: '',
      role: null,
      profilePicId: null,
      identityKey: null,
    }
  }

  const trimmedUsername = username.trim()
  if (!trimmedUsername) {
    return {
      rawUsername: '',
      displayName: '',
      role: null,
      profilePicId: null,
      identityKey: null,
    }
  }

  try {
    const parsedUsername = JSON.parse(trimmedUsername)
    if (parsedUsername && typeof parsedUsername === 'object' && !Array.isArray(parsedUsername)) {
      return {
        rawUsername: trimmedUsername,
        displayName:
          typeof parsedUsername.name === 'string' && parsedUsername.name.trim()
            ? parsedUsername.name.trim()
            : trimmedUsername,
        role:
          typeof parsedUsername.role === 'string' && parsedUsername.role.trim()
            ? parsedUsername.role.trim()
            : null,
        profilePicId:
          typeof parsedUsername.profilePicId === 'string' && parsedUsername.profilePicId.trim()
            ? parsedUsername.profilePicId.trim()
            : null,
        identityKey:
          typeof parsedUsername.identityKey === 'string' && parsedUsername.identityKey.trim()
            ? parsedUsername.identityKey.trim()
            : null,
      }
    }
  } catch (error) {
    const nameMatch = trimmedUsername.match(/"name"\s*:\s*"([^"]+)"/)
    const roleMatch = trimmedUsername.match(/"role"\s*:\s*"([^"]+)"/)
    const identityKeyMatch = trimmedUsername.match(/"identityKey"\s*:\s*"([^"]+)"/)

    return {
      rawUsername: trimmedUsername,
      displayName: nameMatch?.[1]?.trim() || trimmedUsername,
      role: roleMatch?.[1]?.trim() || null,
      profilePicId: null,
      identityKey: identityKeyMatch?.[1]?.trim() || null,
    }
  }

  return {
    rawUsername: trimmedUsername,
    displayName: trimmedUsername,
    role: null,
    profilePicId: null,
    identityKey: null,
  }
}

function resolveUserMetadataForUid({ usernameMap, uid, fallbackUsername = '' }) {
  return parseUsernameMetadata(resolveUsernameForUid(usernameMap, uid) || fallbackUsername)
}

function buildScreenShareUsername({
  username,
  ownerUid,
  fallbackDisplayName = 'You',
  fallbackRole = 'participant',
} = {}) {
  const baseMetadata = parseUsernameMetadata(username)
  const normalizedOwnerUid =
    ownerUid === null || ownerUid === undefined || `${ownerUid}`.trim() === ''
      ? 'local'
      : `${ownerUid}`.trim()

  return JSON.stringify({
    name: baseMetadata.displayName || fallbackDisplayName,
    role: baseMetadata.role || fallbackRole,
    profilePicId: baseMetadata.profilePicId || undefined,
    identityKey: `screen-share:${normalizedOwnerUid}`,
  })
}

function hasMeaningfulParticipantIdentity(metadata) {
  const identityKey =
    typeof metadata?.identityKey === 'string' ? metadata.identityKey.trim() : ''

  if (identityKey) {
    return true
  }

  const displayName =
    typeof metadata?.displayName === 'string' ? metadata.displayName.trim() : ''

  if (typeof metadata?.role === 'string' && metadata.role.trim()) {
    return true
  }

  if (!displayName) {
    return false
  }

  return !/^participant \d+$/i.test(displayName) &&
    !/^user \d+$/i.test(displayName) &&
    displayName.toLowerCase() !== 'guest' &&
    displayName.toLowerCase() !== 'unknown'
}

function createRtcUserDescriptor({
  uid,
  hasAudio,
  hasVideo,
  isLocal = false,
}) {
  return {
    uid,
    isLocal,
    hasAudio: isLocal
      ? hasAudio
        ? TOGGLE_STATES.enabled
        : TOGGLE_STATES.disabled
      : hasAudio
        ? REMOTE_TRACK_STATES.subbed
        : REMOTE_TRACK_STATES.no,
    hasVideo: isLocal
      ? hasVideo
        ? TOGGLE_STATES.enabled
        : TOGGLE_STATES.disabled
      : hasVideo
        ? REMOTE_TRACK_STATES.subbed
        : REMOTE_TRACK_STATES.no,
  }
}

function mergeButtonStyles(styleProps = {}, key) {
  return {
    ...(isPlainObject(styleProps.localBtnStyles) ? styleProps.localBtnStyles : {}),
    ...(styleProps?.[key] ? { [key]: styleProps[key] } : {}),
  }[key]
}

function maybeInvoke(handler, ...args) {
  if (typeof handler === 'function') {
    handler(...args)
  }
}

function useLatestRef(value) {
  const ref = React.useRef(value)

  React.useEffect(() => {
    ref.current = value
  }, [value])

  return ref
}

function buildPresenceState({
  rtcUid,
  username,
  role,
  audioEnabled,
  videoEnabled,
}) {
  return {
    rtcUid: stringifyUid(rtcUid, ''),
    username: typeof username === 'string' ? username : '',
    role: normalizeRole(role),
    audioMuted: audioEnabled ? '0' : '1',
    videoMuted: videoEnabled ? '0' : '1',
  }
}

function shouldPreferRemoteUserCandidate(
  nextUser,
  nextMetadata,
  currentUser,
  currentMetadata,
  preferredRtcUid = null,
) {
  const preferredUidString =
    preferredRtcUid === null || preferredRtcUid === undefined ? null : String(preferredRtcUid)
  const nextUidString = stringifyUid(nextUser?.uid, '')
  const currentUidString = stringifyUid(currentUser?.uid, '')

  if (preferredUidString) {
    const nextMatchesPreferred = nextUidString === preferredUidString
    const currentMatchesPreferred = currentUidString === preferredUidString

    if (nextMatchesPreferred !== currentMatchesPreferred) {
      return nextMatchesPreferred
    }
  }

  const nextHasVideo = Boolean(nextUser?.videoTrack || nextUser?.hasVideo)
  const currentHasVideo = Boolean(currentUser?.videoTrack || currentUser?.hasVideo)
  if (nextHasVideo !== currentHasVideo) {
    return nextHasVideo
  }

  const nextHasAudio = Boolean(nextUser?.audioTrack || nextUser?.hasAudio)
  const currentHasAudio = Boolean(currentUser?.audioTrack || currentUser?.hasAudio)
  if (nextHasAudio !== currentHasAudio) {
    return nextHasAudio
  }

  const nextHasMeaningfulIdentity = hasMeaningfulParticipantIdentity(nextMetadata)
  const currentHasMeaningfulIdentity = hasMeaningfulParticipantIdentity(currentMetadata)
  if (nextHasMeaningfulIdentity !== currentHasMeaningfulIdentity) {
    return nextHasMeaningfulIdentity
  }

  const nextLastActivityAt = Number(nextUser?.lastActivityAt || 0)
  const currentLastActivityAt = Number(currentUser?.lastActivityAt || 0)
  if (nextLastActivityAt !== currentLastActivityAt) {
    return nextLastActivityAt > currentLastActivityAt
  }

  const nextNumericUid = Number(nextUser?.uid)
  const currentNumericUid = Number(currentUser?.uid)
  if (Number.isFinite(nextNumericUid) && Number.isFinite(currentNumericUid)) {
    return nextNumericUid > currentNumericUid
  }

  return false
}

function resolveDominantUser({
  layout,
  pinnedUid,
  activeSpeakerUid,
  localUser,
  remoteUsers,
}) {
  if (layout !== AGORA_LAYOUTS.pin) {
    return null
  }

  const remoteUsersList = remoteUsers.filter((user) => !user.isScreenShare)
  const allUsers = [...remoteUsersList, localUser].filter(Boolean)

  if (allUsers.length === 0) {
    return null
  }

  if (pinnedUid !== null && pinnedUid !== undefined) {
    const pinnedUser = allUsers.find((user) => String(user.uid) === String(pinnedUid))
    if (pinnedUser) {
      return pinnedUser
    }
  }

  if (activeSpeakerUid !== null && activeSpeakerUid !== undefined) {
    const activeSpeakerUser = allUsers.find(
      (user) => String(user.uid) === String(activeSpeakerUid),
    )
    if (activeSpeakerUser) {
      return activeSpeakerUser
    }
  }

  const remoteUserWithVideo = remoteUsersList.find((user) => user.hasVideo)
  if (remoteUserWithVideo) {
    return remoteUserWithVideo
  }

  return remoteUsersList[0] || localUser || allUsers[0]
}

function parsePresenceMutedState(value, fallbackValue = null) {
  if (value === '1' || value === 1 || value === true) {
    return true
  }

  if (value === '0' || value === 0 || value === false) {
    return false
  }

  return fallbackValue
}

async function applyPlaybackDeviceToTrack(track, deviceId) {
  if (!track || !deviceId || typeof track.setPlaybackDevice !== 'function') {
    return
  }

  try {
    await track.setPlaybackDevice(deviceId)
  } catch (error) {
    console.warn('[AgoraRtcSurface] Failed to apply playback device', error)
  }
}

function DefaultVideoPlaceholder({
  user,
  username,
  isHovered,
  showRemoteControls,
  onRemoteMuteAudio,
  onRemoteMuteVideo,
  presentationTone = 'default',
}) {
  const hasAudio = user?.hasAudio === true || user?.hasAudio === TOGGLE_STATES.enabled
  const isChamberPresentation = presentationTone === 'chamber'

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '24px',
        boxSizing: 'border-box',
        background: isChamberPresentation
          ? 'radial-gradient(circle at top, rgba(30, 41, 59, 0.84) 0%, rgba(2, 6, 23, 0.98) 56%, rgba(2, 6, 23, 1) 100%)'
          : 'radial-gradient(circle at top, rgba(233, 213, 255, 0.7) 0%, rgba(255, 255, 255, 0.98) 44%, rgba(243, 232, 255, 0.94) 100%)',
        color: isChamberPresentation ? '#f8fafc' : '#4c1d95',
      }}
    >
      <div
        style={{
          width: '96px',
          height: '96px',
          borderRadius: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isChamberPresentation
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.94) 0%, rgba(30, 41, 59, 0.94) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(243, 232, 255, 0.98) 100%)',
          boxShadow: isChamberPresentation
            ? '0 22px 48px rgba(2, 6, 23, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
            : '0 22px 48px rgba(139, 92, 246, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.94)',
          border: isChamberPresentation
            ? '1px solid rgba(226, 232, 240, 0.12)'
            : '1px solid rgba(196, 181, 253, 0.88)',
        }}
      >
        <VideocamOffRoundedIcon
          sx={{
            fontSize: 42,
            color: isChamberPresentation ? '#cbd5e1' : '#7c3aed',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          padding: '10px 16px',
          borderRadius: '999px',
          background: isChamberPresentation
            ? 'rgba(15, 23, 42, 0.84)'
            : 'rgba(255, 255, 255, 0.92)',
          border: isChamberPresentation
            ? '1px solid rgba(226, 232, 240, 0.14)'
            : '1px solid rgba(196, 181, 253, 0.74)',
          boxShadow: isChamberPresentation
            ? '0 14px 30px rgba(2, 6, 23, 0.26)'
            : '0 14px 30px rgba(139, 92, 246, 0.12)',
          maxWidth: 'calc(100% - 32px)',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: isChamberPresentation ? '#f8fafc' : '#4c1d95',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {username || 'Guest'}
        </div>
        {hasAudio ? (
          <MicRoundedIcon sx={{ fontSize: 18, color: '#0f766e' }} />
        ) : (
          <MicOffRoundedIcon sx={{ fontSize: 18, color: '#be123c' }} />
        )}
      </div>
      {showRemoteControls && isHovered ? (
        <div
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            zIndex: 3,
          }}
        >
          <button
            type="button"
            onClick={onRemoteMuteVideo}
            style={remoteControlButtonStyle}
            title="Mute remote video"
          >
            <VideocamOffRoundedIcon sx={{ fontSize: 18 }} />
          </button>
          <button
            type="button"
            onClick={onRemoteMuteAudio}
            style={remoteControlButtonStyle}
            title="Mute remote audio"
          >
            <VolumeOffRoundedIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
      ) : null}
    </div>
  )
}

function renderVideoPlaceholder({
  CustomVideoPlaceholder,
  user,
  username,
  localUsername,
  isHovered,
  showRemoteControls,
  onRemoteMuteAudio,
  onRemoteMuteVideo,
  presentationTone,
}) {
  if (typeof CustomVideoPlaceholder === 'function') {
    return (
      <CustomVideoPlaceholder
        user={user}
        isShown={isHovered}
        showButtons={showRemoteControls}
        showSwap={false}
        uidToUsernameMap={{ [user.uid]: username }}
        localRtmUsername={localUsername}
      />
    )
  }

  return (
    <DefaultVideoPlaceholder
      user={user}
      username={username}
      isHovered={isHovered}
      showRemoteControls={showRemoteControls}
      onRemoteMuteAudio={onRemoteMuteAudio}
      onRemoteMuteVideo={onRemoteMuteVideo}
      presentationTone={presentationTone}
    />
  )
}

function SafeAgoraVideoPlayer({ videoTrack, config, style }) {
  const videoRef = React.useRef(null)
  const fit = config?.fit || 'cover'
  const shouldMirror = config?.mirror === true
  const bindingToken = config?.bindingToken

  React.useLayoutEffect(() => {
    const videoElement = videoRef.current
    const mediaStreamTrack = videoTrack?.getMediaStreamTrack?.()

    if (!videoElement || !mediaStreamTrack) {
      return undefined
    }

    const mediaStream = new window.MediaStream([mediaStreamTrack])
    videoElement.srcObject = mediaStream
    videoElement.muted = true
    videoElement.defaultMuted = true
    videoElement.playsInline = true
    videoElement.autoplay = true

    try {
      const playPromise = videoElement.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((error) => {
          console.warn('[AgoraRtcSurface] Failed to start video element playback', error)
        })
      }
    } catch (error) {
      console.warn('[AgoraRtcSurface] Failed to bind video track', error)
    }

    return () => {
      try {
        videoElement.pause()
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to pause video element cleanly', error)
      }

      if (videoElement.srcObject === mediaStream) {
        videoElement.srcObject = null
      }
    }
  }, [bindingToken, videoTrack])

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      style={{
        ...style,
        objectFit: fit,
        transform: shouldMirror ? 'scaleX(-1)' : style?.transform,
      }}
    />
  )
}

function VideoCard({
  user,
  isLocal = false,
  isDominant = false,
  username,
  localUsername,
  participantRole = null,
  hasRtmMetadata = false,
  videoTrack,
  renderMode = 'cover',
  styleProps,
  CustomVideoPlaceholder,
  canRemoteMute = false,
  onRemoteMuteAudio = noop,
  onRemoteMuteVideo = noop,
  onPin = noop,
  showPinControl = true,
  enableHoverInteractions = true,
  localPreviewBindingToken = 0,
  presentationTone = 'default',
  participantRoleLabel = '',
  showParticipantRoleBadge = false,
  useParticipantMetaOverlay = false,
}) {
  const [isHovered, setIsHovered] = React.useState(false)
  const usernameTextStyle = styleProps?.usernameText || null
  const audioEnabled =
    user?.hasAudio === true || user?.hasAudio === TOGGLE_STATES.enabled
  const videoEnabled =
    user?.hasVideo === true || user?.hasVideo === TOGGLE_STATES.enabled
  const shouldRenderVideo =
    Boolean(videoTrack) &&
    user?.hasVideo !== false &&
    user?.hasVideo !== TOGGLE_STATES.disabled
  const statusPills = []
  const shouldRenderParticipantMeta = Boolean(shouldRenderVideo && username)
  const shouldRenderRoleBadge =
    Boolean(useParticipantMetaOverlay) &&
    Boolean(showParticipantRoleBadge) &&
    Boolean(participantRoleLabel)

  if (!videoEnabled) {
    statusPills.push({
      key: 'camera-off',
      label: 'Camera off',
      icon: <VideocamOffRoundedIcon sx={{ fontSize: 14 }} />,
      tone: 'camera',
    })
  }

  if (!audioEnabled) {
    statusPills.push({
      key: 'mic-muted',
      label: 'Mic off',
      icon: <MicOffRoundedIcon sx={{ fontSize: 14 }} />,
      tone: 'audio',
    })
  }

  return (
    <div
      data-uid={user.uid}
      data-local-user={isLocal ? 'true' : 'false'}
      data-dominant={isDominant ? 'true' : 'false'}
      data-role={participantRole || (isLocal ? 'local' : 'participant')}
      data-has-rtm={hasRtmMetadata ? 'true' : 'false'}
      className="agora-rtc-user-card"
      style={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: isDominant ? '20px' : '16px',
        background:
          'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(243, 232, 255, 0.94) 100%)',
        boxShadow: isDominant
          ? 'none'
          : 'inset 0 0 0 1px rgba(216, 180, 254, 0.46)',
      }}
      onMouseEnter={enableHoverInteractions ? () => setIsHovered(true) : undefined}
      onMouseLeave={enableHoverInteractions ? () => setIsHovered(false) : undefined}
    >
      {shouldRenderVideo ? (
        <SafeAgoraVideoPlayer
          videoTrack={videoTrack}
          config={{
            fit: renderMode,
            mirror: isLocal,
            bindingToken: isLocal ? localPreviewBindingToken : undefined,
          }}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            background:
              'linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(243, 232, 255, 0.92) 100%)',
          }}
        />
      ) : (
        renderVideoPlaceholder({
          CustomVideoPlaceholder,
          user: createRtcUserDescriptor({
            uid: user.uid,
            hasAudio: user.hasAudio,
            hasVideo: user.hasVideo,
            isLocal,
          }),
          username,
          localUsername,
          isHovered,
          showRemoteControls: canRemoteMute && !isLocal,
          onRemoteMuteAudio,
          onRemoteMuteVideo,
          presentationTone,
        })
      )}
      {shouldRenderParticipantMeta ? (
        useParticipantMetaOverlay ? (
          <div
            className="agora-rtc-participant-meta"
            data-has-role={shouldRenderRoleBadge ? 'true' : 'false'}
          >
            {shouldRenderRoleBadge ? (
              <span className="agora-rtc-participant-role">{participantRoleLabel}</span>
            ) : null}
            <p
              className="agora-rtc-username"
              style={{
                margin: 0,
                ...(usernameTextStyle || {}),
              }}
            >
              {username}
            </p>
          </div>
        ) : (
          <p
            className="agora-rtc-username"
            style={{
              ...defaultUsernameTextStyle,
              ...(usernameTextStyle || {}),
            }}
          >
            {username}
          </p>
        )
      ) : null}
      {shouldRenderVideo && statusPills.length ? (
        <div
          className="agora-rtc-status-row"
          style={defaultStatusRowStyle}
        >
          {statusPills.map((pill) => (
            <span
              key={pill.key}
              className={`agora-rtc-status-pill agora-rtc-status-pill-${pill.tone}`}
              style={{
                ...defaultStatusPillStyle,
                background:
                  pill.tone === 'camera'
                    ? 'rgba(255, 241, 242, 0.94)'
                    : 'rgba(240, 253, 250, 0.94)',
                border:
                  pill.tone === 'camera'
                    ? '1px solid rgba(251, 113, 133, 0.32)'
                    : '1px solid rgba(45, 212, 191, 0.28)',
                color: pill.tone === 'camera' ? '#be123c' : '#0f766e',
              }}
            >
              {pill.icon}
              {pill.label}
            </span>
          ))}
        </div>
      ) : null}
      {!isLocal && showPinControl && isHovered ? (
        <div
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            display: 'flex',
            gap: '8px',
            zIndex: 3,
          }}
        >
          <button
            type="button"
            onClick={onPin}
            style={remoteControlButtonStyle}
            title="Pin participant"
          >
            <PushPinRoundedIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
      ) : null}
    </div>
  )
}

function areVideoCardPropsEqual(previousProps, nextProps) {
  return (
    previousProps.isLocal === nextProps.isLocal &&
    previousProps.isDominant === nextProps.isDominant &&
    previousProps.username === nextProps.username &&
    previousProps.localUsername === nextProps.localUsername &&
    previousProps.participantRole === nextProps.participantRole &&
    previousProps.hasRtmMetadata === nextProps.hasRtmMetadata &&
    previousProps.videoTrack === nextProps.videoTrack &&
    previousProps.renderMode === nextProps.renderMode &&
    previousProps.styleProps === nextProps.styleProps &&
    previousProps.CustomVideoPlaceholder === nextProps.CustomVideoPlaceholder &&
    previousProps.canRemoteMute === nextProps.canRemoteMute &&
    previousProps.showPinControl === nextProps.showPinControl &&
    previousProps.enableHoverInteractions === nextProps.enableHoverInteractions &&
    previousProps.localPreviewBindingToken === nextProps.localPreviewBindingToken &&
    previousProps.presentationTone === nextProps.presentationTone &&
    previousProps.participantRoleLabel === nextProps.participantRoleLabel &&
    previousProps.showParticipantRoleBadge === nextProps.showParticipantRoleBadge &&
    previousProps.useParticipantMetaOverlay === nextProps.useParticipantMetaOverlay &&
    previousProps.user?.uid === nextProps.user?.uid &&
    previousProps.user?.hasAudio === nextProps.user?.hasAudio &&
    previousProps.user?.hasVideo === nextProps.user?.hasVideo &&
    previousProps.user?.audioTrack === nextProps.user?.audioTrack &&
    previousProps.user?.videoTrack === nextProps.user?.videoTrack &&
    previousProps.user?.isScreenShare === nextProps.user?.isScreenShare
  )
}

const MemoVideoCard = React.memo(VideoCard, areVideoCardPropsEqual)

function LocalControlButton({
  title,
  active,
  onClick,
  style,
  dataRole,
  children,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-role={dataRole}
      data-custom-state={active ? 'enabled' : 'disabled'}
      style={{
        ...defaultLocalButtonStyle,
        ...(style || {}),
      }}
    >
      {children}
    </button>
  )
}

function SettingsPopover({
  anchorEl,
  onClose,
  deviceLists,
  selectedDevices,
  onSelectDevice,
  chamberKioskTheme = false,
}) {
  const open = Boolean(anchorEl)
  const S = chamberKioskTheme ? chamberKioskDeviceSettingsTheme : defaultDeviceSettingsTheme

  const deviceGroups = [
    {
      key: 'cameraDeviceId',
      label: 'Camera',
      emptyLabel: 'Default camera',
      devices: deviceLists.cameras,
    },
    {
      key: 'microphoneDeviceId',
      label: 'Microphone',
      emptyLabel: 'Default microphone',
      devices: deviceLists.microphones,
    },
    {
      key: 'playbackDeviceId',
      label: 'Speaker',
      emptyLabel: 'Default speaker',
      devices: deviceLists.speakers,
    },
  ]

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: S.paperSx,
      }}
    >
      <div style={S.dialog}>
        <div style={S.headerStyle}>
          <div>
            <div style={S.eyebrowStyle}>Management</div>
            <strong style={S.titleStyle}>Device Settings</strong>
          </div>
          <button type="button" onClick={onClose} style={S.closeButtonStyle}>
            Close
          </button>
        </div>
        <div style={S.bodyStyle}>
          <div style={S.summaryStyle}>
            Pick the kiosk camera, microphone, and speaker for this room.
          </div>
          {deviceGroups.map((group) => (
            <div key={group.key} style={S.groupStyle}>
              <div style={S.fieldHeaderStyle}>
                <span>{group.label}</span>
                <span style={S.fieldMetaStyle}>
                  {group.devices.length || 0} available
                </span>
              </div>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  value={selectedDevices[group.key] || ''}
                  displayEmpty
                  onChange={(event) => onSelectDevice(group.key, event.target.value)}
                  MenuProps={S.selectMenuProps}
                  sx={S.selectSx}
                >
                  <MenuItem value="">{group.emptyLabel}</MenuItem>
                  {group.devices.map((device) => (
                    <MenuItem key={device.deviceId} value={device.deviceId}>
                      {device.label || group.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          ))}
        </div>
      </div>
    </Popover>
  )
}

function RemoteMuteDialog({
  pendingMuteRequest,
  onAccept,
  onDecline,
}) {
  if (!pendingMuteRequest) {
    return null
  }

  const targetDevice =
    pendingMuteRequest.device === REMOTE_MUTING_DEVICES.camera ? 'camera' : 'microphone'
  const actionLabel = pendingMuteRequest.mute ? `Mute ${targetDevice}` : `Unmute ${targetDevice}`

  return (
    <div style={remoteMuteDialogContainerStyle}>
      <div style={remoteMuteDialogCardStyle}>
        <div style={{ fontWeight: 600 }}>Remote request</div>
        <div style={{ fontSize: '14px', color: '#cbd5f5' }}>
          Another participant wants to {actionLabel.toLowerCase()}.
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={onDecline} style={remoteMuteSecondaryButtonStyle}>
            Decline
          </button>
          <button type="button" onClick={onAccept} style={remoteMutePrimaryButtonStyle}>
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AgoraRtcSurface({
  fallback = null,
  rtcProps = {},
  rtmProps,
  styleProps = {},
  callbacks = {},
  rtmCallbacks = {},
  renderless = false,
  children = null,
}) {
  const resolvedRtcProps = React.useMemo(
    () => {
      const resolvedVideoQuality = normalizeResolvedVideoCallQuality(rtcProps?.videoQuality)

      return {
        ...rtcProps,
        appId: rtcProps?.appId,
        channel: rtcProps?.channel,
        uid: normalizeUid(rtcProps?.uid),
        layout: normalizeLayout(rtcProps?.layout),
        role: normalizeRole(rtcProps?.role),
        activeSpeaker: normalizeBoolean(rtcProps?.activeSpeaker, false),
        callActive: normalizeBoolean(rtcProps?.callActive, true),
        enableDualStream: normalizeBoolean(
          rtcProps?.enableDualStream,
          resolvedVideoQuality?.enableDualStream === true,
        ),
        enableScreensharing: normalizeBoolean(rtcProps?.enableScreensharing, false),
        enableAudio: normalizeBoolean(rtcProps?.enableAudio, true),
        enableVideo: normalizeBoolean(rtcProps?.enableVideo, true),
        disableRtm: normalizeBoolean(rtcProps?.disableRtm, !rtmProps),
        videoQuality: resolvedVideoQuality,
      }
    },
    [rtcProps, rtmProps],
  )
  const latestRtcPropsRef = useLatestRef(resolvedRtcProps)
  const latestCallbacksRef = useLatestRef(callbacks)
  const latestRtmCallbacksRef = useLatestRef(rtmCallbacks)
  const latestRtmPropsRef = useLatestRef(rtmProps)
  const [remoteUsersMap, setRemoteUsersMap] = React.useState({})
  const remoteUsersMapRef = React.useRef({})
  const [localVideoTrack, setLocalVideoTrack] = React.useState(null)
  const [localPreviewBindingToken, setLocalPreviewBindingToken] = React.useState(0)
  const [localAudioTrack, setLocalAudioTrack] = React.useState(null)
  const [localMediaIntent, setLocalMediaIntent] = React.useState({
    audio: resolvedRtcProps.enableAudio,
    video: resolvedRtcProps.enableVideo,
  })
  const [deviceLists, setDeviceLists] = React.useState({
    cameras: [],
    microphones: [],
    speakers: [],
  })
  const [selectedDevices, setSelectedDevices] = React.useState({
    cameraDeviceId: resolvedRtcProps.cameraDeviceId || '',
    microphoneDeviceId: resolvedRtcProps.microphoneDeviceId || '',
    playbackDeviceId: resolvedRtcProps.playbackDeviceId || '',
  })
  const [usernameMap, setUsernameMap] = React.useState({})
  const usernameMapRef = React.useRef({})
  const [, setPresenceStateByUid] = React.useState({})
  const [uidMap, setUidMap] = React.useState({})
  const uidMapRef = React.useRef({})
  const reverseUidMapRef = React.useRef({})
  const stableIdentityToRtcUidRef = React.useRef({})
  const rtcUidToStableIdentityRef = React.useRef({})
  const presenceIdentityByUserIdRef = React.useRef({})
  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState(null)
  const [pendingMuteRequest, setPendingMuteRequest] = React.useState(null)
  const [activeSpeakerUid, setActiveSpeakerUid] = React.useState(null)
  const [pinnedUid, setPinnedUid] = React.useState(null)
  const [screenShareActive, setScreenShareActive] = React.useState(false)
  const [joiningRtc, setJoiningRtc] = React.useState(false)
  const [rtcReady, setRtcReady] = React.useState(false)
  // Per-device acquisition failures surfaced to the user (null when the device is fine).
  // Shape: { camera: { reason } | null, microphone: { reason } | null }.
  const [deviceError, setDeviceError] = React.useState({ camera: null, microphone: null })
  const clientRef = React.useRef(null)
  const joinedRef = React.useRef(false)
  const screenClientRef = React.useRef(null)
  const screenTrackRef = React.useRef(null)
  const localAudioTrackRef = React.useRef(null)
  const localVideoTrackRef = React.useRef(null)
  const createdAudioTrackRef = React.useRef(false)
  const createdVideoTrackRef = React.useRef(false)
  const creatingAudioTrackRef = React.useRef(null)
  const creatingVideoTrackRef = React.useRef(null)
  const rtmClientRef = React.useRef(null)
  const rtmConnectedRef = React.useRef(false)
  const destroyedRef = React.useRef(false)
  const localTrackSyncPromiseRef = React.useRef(Promise.resolve())
  // Doctor-side mic resilience: recovers a local mic track the OS/driver kills mid-call.
  const recoverLocalAudioRef = React.useRef(null)
  const lastMicRecoveryAtRef = React.useRef(0)

  React.useEffect(() => {
    remoteUsersMapRef.current = remoteUsersMap
  }, [remoteUsersMap])

  React.useEffect(() => {
    usernameMapRef.current = usernameMap
  }, [usernameMap])

  React.useEffect(() => {
    uidMapRef.current = uidMap
  }, [uidMap])

  React.useEffect(() => {
    localAudioTrackRef.current = localAudioTrack
  }, [localAudioTrack])

  React.useEffect(() => {
    localVideoTrackRef.current = localVideoTrack
  }, [localVideoTrack])

  React.useEffect(() => {
    setLocalMediaIntent({
      audio: resolvedRtcProps.enableAudio,
      video: resolvedRtcProps.enableVideo,
    })
  }, [resolvedRtcProps.enableAudio, resolvedRtcProps.enableVideo])

  React.useEffect(() => {
    setSelectedDevices((previousState) => ({
      cameraDeviceId: resolvedRtcProps.cameraDeviceId || previousState.cameraDeviceId || '',
      microphoneDeviceId:
        resolvedRtcProps.microphoneDeviceId || previousState.microphoneDeviceId || '',
      playbackDeviceId:
        resolvedRtcProps.playbackDeviceId || previousState.playbackDeviceId || '',
    }))
  }, [
    resolvedRtcProps.cameraDeviceId,
    resolvedRtcProps.microphoneDeviceId,
    resolvedRtcProps.playbackDeviceId,
  ])

  const refreshDevices = React.useCallback(async () => {
    try {
      const [cameras, microphones, speakers] = await Promise.all([
        AgoraRTC.getCameras().catch(() => []),
        AgoraRTC.getMicrophones().catch(() => []),
        AgoraRTC.getPlaybackDevices().catch(() => []),
      ])

      setDeviceLists({
        cameras,
        microphones,
        speakers,
      })
    } catch (error) {
      console.warn('[AgoraRtcSurface] Failed to enumerate devices', error)
    }
  }, [])

  const applyRemoteParticipantEvent = React.useCallback((event) => {
    setRemoteUsersMap((previousState) =>
      reduceAgoraRemoteParticipants(previousState, event),
    )
  }, [])

  const removeRemoteUser = React.useCallback((uid) => {
    const uidKey = stringifyUid(uid)

    setRemoteUsersMap((previousState) => {
      if (!previousState[uidKey]) {
        return previousState
      }

      const nextState = { ...previousState }
      delete nextState[uidKey]
      return nextState
    })
  }, [])

  const retirePresenceRtcUid = React.useCallback(
    (
      rtcUid,
      {
        removeRemote = true,
        preserveRtcMetadata = false,
      } = {},
    ) => {
      const normalizedRtcUid = normalizeUid(rtcUid, null)

      if (normalizedRtcUid === null) {
        return
      }

      const stableIdentityKey =
        rtcUidToStableIdentityRef.current[stringifyUid(normalizedRtcUid)] ||
        parseUsernameMetadata(resolveUsernameForUid(usernameMapRef.current, normalizedRtcUid))
          .identityKey

      setUidMap((previousState) => {
        const nextState = { ...previousState }
        getAllUidKeys(normalizedRtcUid).forEach((key) => {
          delete nextState[key]
        })
        return nextState
      })

      if (!preserveRtcMetadata) {
        setUsernameMap((previousState) => {
          const nextState = { ...previousState }
          getAllUidKeys(normalizedRtcUid).forEach((key) => {
            delete nextState[key]
          })
          return nextState
        })
      }
      setPresenceStateByUid((previousState) => {
        const nextState = { ...previousState }
        getAllUidKeys(normalizedRtcUid).forEach((key) => {
          delete nextState[key]
        })
        return nextState
      })

      Object.entries(reverseUidMapRef.current).forEach(([mappedUserId, mappedRtcUid]) => {
        if (String(mappedRtcUid) !== String(normalizedRtcUid)) {
          return
        }

        delete reverseUidMapRef.current[mappedUserId]
        delete presenceIdentityByUserIdRef.current[mappedUserId]
      })

      if (!preserveRtcMetadata) {
        getAllUidKeys(normalizedRtcUid).forEach((key) => {
          delete rtcUidToStableIdentityRef.current[stringifyUid(key)]
        })
      }

      if (
        !preserveRtcMetadata &&
        stableIdentityKey &&
        stableIdentityToRtcUidRef.current[stableIdentityKey] === normalizedRtcUid
      ) {
        delete stableIdentityToRtcUidRef.current[stableIdentityKey]
      }

      if (removeRemote) {
        removeRemoteUser(normalizedRtcUid)
      }
    },
    [removeRemoteUser],
  )

  const updatePresenceParticipant = React.useCallback((userId, states = {}) => {
    const rtcUid =
      normalizeUid(states?.rtcUid, null) ??
      normalizeUid(states?.rtcId, null) ??
      normalizeUid(userId, null)

    if (rtcUid === null) {
      return
    }

    const username = states?.username
    const metadata = parseUsernameMetadata(username)
    const stableIdentityKey = metadata.identityKey
    const previouslyMappedRtcUid = userId
      ? normalizeUid(reverseUidMapRef.current[userId], null)
      : null

    if (previouslyMappedRtcUid !== null && previouslyMappedRtcUid !== rtcUid) {
      retirePresenceRtcUid(
        previouslyMappedRtcUid,
        resolveAgoraPresenceRetireOptions(
          remoteUsersMapRef.current,
          previouslyMappedRtcUid,
        ),
      )
    }

    if (stableIdentityKey) {
      const previouslyMappedIdentityRtcUid = normalizeUid(
        stableIdentityToRtcUidRef.current[stableIdentityKey],
        null,
      )

      if (
        previouslyMappedIdentityRtcUid !== null &&
        previouslyMappedIdentityRtcUid !== rtcUid
      ) {
        retirePresenceRtcUid(
          previouslyMappedIdentityRtcUid,
          resolveAgoraPresenceRetireOptions(
            remoteUsersMapRef.current,
            previouslyMappedIdentityRtcUid,
          ),
        )
      }

      stableIdentityToRtcUidRef.current[stableIdentityKey] = rtcUid
      getAllUidKeys(rtcUid).forEach((key) => {
        rtcUidToStableIdentityRef.current[stringifyUid(key)] = stableIdentityKey
      })
    }

    if (username) {
      setUsernameMap((previousState) => {
        const nextState = { ...previousState }
        getAllUidKeys(rtcUid).forEach((key) => {
          nextState[key] = username
        })
        return nextState
      })
    }

    setPresenceStateByUid((previousState) => {
      const nextState = { ...previousState }
      const currentPresenceState =
        previousState[String(rtcUid)] || previousState[rtcUid] || {}

      getAllUidKeys(rtcUid).forEach((key) => {
        nextState[key] = {
          ...currentPresenceState,
          audioMuted: parsePresenceMutedState(
            states?.audioMuted,
            currentPresenceState?.audioMuted ?? false,
          ),
          videoMuted: parsePresenceMutedState(
            states?.videoMuted,
            currentPresenceState?.videoMuted ?? true,
          ),
        }
      })

      return nextState
    })

    if (userId) {
      setUidMap((previousState) => ({
        ...previousState,
        [rtcUid]: userId,
        [String(rtcUid)]: userId,
      }))
      reverseUidMapRef.current[userId] = rtcUid
      if (stableIdentityKey) {
        presenceIdentityByUserIdRef.current[userId] = stableIdentityKey
      }
    }

    if (username) {
      maybeInvoke(latestCallbacksRef.current?.['rtm-user-joined'], rtcUid, username, states)
    }
  }, [latestCallbacksRef, retirePresenceRtcUid])

  const removePresenceParticipant = React.useCallback((userId) => {
    const mappedRtcUid =
      reverseUidMapRef.current[userId] ??
      uidMapRef.current[userId] ??
      normalizeUid(userId, null)

    if (mappedRtcUid === null) {
      delete reverseUidMapRef.current[userId]
      delete presenceIdentityByUserIdRef.current[userId]
      return
    }

    delete reverseUidMapRef.current[userId]
    delete presenceIdentityByUserIdRef.current[userId]
    retirePresenceRtcUid(
      mappedRtcUid,
      resolveAgoraPresenceRetireOptions(remoteUsersMapRef.current, mappedRtcUid),
    )

    maybeInvoke(latestCallbacksRef.current?.['rtm-user-left'], mappedRtcUid)
  }, [latestCallbacksRef, retirePresenceRtcUid])

  const updateLocalPresence = React.useCallback(async () => {
    const currentRtmClient = rtmClientRef.current
    const currentRtmProps = latestRtmPropsRef.current
    const currentRtcProps = latestRtcPropsRef.current

    if (!currentRtmClient || !rtmConnectedRef.current || currentRtcProps.disableRtm) {
      return
    }

    try {
      await currentRtmClient.presence.setState(
        currentRtcProps.channel,
        RTM_CHANNEL_TYPE,
        buildPresenceState({
          rtcUid: currentRtcProps.uid,
          username: currentRtmProps?.username,
          role: currentRtcProps.role,
          audioEnabled: localMediaIntent.audio,
          videoEnabled: localMediaIntent.video,
        }),
      )
    } catch (error) {
      console.warn('[AgoraRtcSurface] Failed to update RTM presence state', error)
    }
  }, [latestRtmPropsRef, latestRtcPropsRef, localMediaIntent.audio, localMediaIntent.video])

  const applyPlaybackDeviceToRemoteTracks = React.useCallback(
    async (deviceId) => {
      await Promise.all(
        Object.values(remoteUsersMapRef.current).map((remoteUser) =>
          applyPlaybackDeviceToTrack(remoteUser.audioTrack, deviceId),
        ),
      )
    },
    [],
  )

  const syncPlaybackDevice = React.useCallback(async () => {
    if (!selectedDevices.playbackDeviceId) {
      return
    }

    await applyPlaybackDeviceToRemoteTracks(selectedDevices.playbackDeviceId)
  }, [applyPlaybackDeviceToRemoteTracks, selectedDevices.playbackDeviceId])

  // --- Remote-audio playback resilience -------------------------------------------------
  // Low-end Windows chamber kiosks intermittently stop *playing* the remote (doctor's) audio
  // mid-call — a stalled audio element, or a suspended AudioContext after a glitch/reconnect.
  // Operators "fix" it by re-picking a device, which re-kicks the audio engine. This automates
  // that: a watchdog re-plays any remote audio track that has stopped and re-pins the chosen
  // speaker, and we resume playback on the next user gesture if the browser blocked autoplay.
  const playbackDeviceRef = useLatestRef(
    selectedDevices.playbackDeviceId || resolvedRtcProps.playbackDeviceId || '',
  )

  const recoverRemoteAudioPlayback = React.useCallback(
    ({ force = false } = {}) => {
      const activeClient = clientRef.current
      if (!activeClient || !Array.isArray(activeClient.remoteUsers)) {
        return
      }
      const deviceId = playbackDeviceRef.current
      activeClient.remoteUsers.forEach((remoteUser) => {
        const audioTrack = remoteUser?.audioTrack
        if (!audioTrack) {
          return
        }
        // `isPlaying` is false when the local audio element has stopped/detached — the exact
        // state that leaves the chamber silent. Only act then (or when forced), so a healthy
        // call is never disturbed.
        const isPlaying =
          typeof audioTrack.isPlaying === 'boolean' ? audioTrack.isPlaying : true
        if (!force && isPlaying) {
          return
        }
        try {
          audioTrack.play()
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to (re)play remote audio track', error)
        }
        if (deviceId) {
          applyPlaybackDeviceToTrack(audioTrack, deviceId)
        }
      })
    },
    [playbackDeviceRef],
  )

  React.useEffect(() => {
    if (!rtcReady) {
      return undefined
    }

    // If the browser blocked audio autoplay, resume on the next user interaction.
    const handleAutoplayFailed = () => {
      const resumeOnGesture = () => {
        recoverRemoteAudioPlayback({ force: true })
        document.removeEventListener('click', resumeOnGesture)
        document.removeEventListener('touchend', resumeOnGesture)
        document.removeEventListener('keydown', resumeOnGesture)
      }
      document.addEventListener('click', resumeOnGesture)
      document.addEventListener('touchend', resumeOnGesture)
      document.addEventListener('keydown', resumeOnGesture)
    }

    const previousAutoplayHandler = AgoraRTC.onAutoplayFailed
    AgoraRTC.onAutoplayFailed = handleAutoplayFailed

    // Watchdog: re-kick any remote audio track that has stopped playing.
    const watchdogId = setInterval(() => {
      recoverRemoteAudioPlayback()
    }, 4000)

    return () => {
      clearInterval(watchdogId)
      if (AgoraRTC.onAutoplayFailed === handleAutoplayFailed) {
        AgoraRTC.onAutoplayFailed = previousAutoplayHandler
      }
    }
  }, [rtcReady, recoverRemoteAudioPlayback])

  const clearDeviceError = React.useCallback((kind) => {
    setDeviceError((previousState) =>
      previousState[kind] ? { ...previousState, [kind]: null } : previousState,
    )
  }, [])

  const recordDeviceError = React.useCallback((kind, error) => {
    if (destroyedRef.current) {
      return
    }
    setDeviceError((previousState) => ({
      ...previousState,
      [kind]: { reason: classifyMediaError(error) },
    }))
  }, [])

  // Create a local track with resilience against the transient macOS/Chrome failures above.
  // `createTrack(useDefaultDevice)` builds the track; when `useDefaultDevice` is true it must
  // omit the saved device id so the OS default is used. Recovery strategy:
  //   - first failure with a saved device id  -> immediately retry on the default device
  //     (a stale / unplugged / missing saved device is a common cause of NotFound /
  //      Overconstrained / NotReadable even though a working default exists)
  //   - transient failures (device busy)      -> pause briefly and retry
  //   - permission denied                      -> give up immediately (the user must act)
  //   - terminal failure with no fallback left -> give up (e.g. genuinely no device present)
  const acquireLocalTrackWithRetry = React.useCallback(
    async (kind, createTrack, hasPreferredDevice) => {
      let lastError = null

      for (let attempt = 0; attempt < MEDIA_ACQUIRE_MAX_ATTEMPTS; attempt += 1) {
        if (destroyedRef.current) {
          const abortError = new Error('AgoraRtcSurface destroyed during media acquisition')
          abortError.name = 'AbortError'
          throw abortError
        }

        // Once we've failed once, stop trusting the saved device id and use the OS default.
        const useDefaultDevice = attempt > 0

        try {
          return await createTrack(useDefaultDevice)
        } catch (error) {
          lastError = error
          const reason = classifyMediaError(error)
          const canFallBackToDefault = !useDefaultDevice && hasPreferredDevice
          const isLastAttempt = attempt === MEDIA_ACQUIRE_MAX_ATTEMPTS - 1

          if (
            reason === MEDIA_ERROR_REASONS.permission ||
            isLastAttempt ||
            (!isRetryableMediaError(reason) && !canFallBackToDefault)
          ) {
            throw error
          }

          console.warn(
            `[AgoraRtcSurface] Local ${kind} acquisition failed (attempt ${attempt + 1}/${MEDIA_ACQUIRE_MAX_ATTEMPTS}); retrying`,
            error,
          )

          // Dropping a bad device id can retry right away; a busy device needs a moment.
          if (!canFallBackToDefault) {
            await wait(MEDIA_ACQUIRE_RETRY_DELAY_MS)
          }
        }
      }

      throw lastError
    },
    [],
  )

  const ensureVideoTrack = React.useCallback(async () => {
    if (localVideoTrackRef.current) {
      return localVideoTrackRef.current
    }

    if (!creatingVideoTrackRef.current) {
      const currentRtcProps = latestRtcPropsRef.current
      const encoderConfig = buildAgoraEncoderConfig(currentRtcProps?.videoQuality)
      const preferredCameraId = selectedDevices.cameraDeviceId

      creatingVideoTrackRef.current = acquireLocalTrackWithRetry(
        'camera',
        (useDefaultDevice) => {
          const nextTrackConfig = {}

          if (preferredCameraId && !useDefaultDevice) {
            nextTrackConfig.cameraId = preferredCameraId
          }

          if (encoderConfig) {
            nextTrackConfig.encoderConfig = encoderConfig
            nextTrackConfig.optimizationMode = 'motion'
          }

          return AgoraRTC.createCameraVideoTrack(
            Object.keys(nextTrackConfig).length > 0 ? nextTrackConfig : undefined,
          )
        },
        Boolean(preferredCameraId),
      )
        .then((nextTrack) => {
          createdVideoTrackRef.current = true
          setLocalVideoTrack(nextTrack)
          localVideoTrackRef.current = nextTrack
          clearDeviceError('camera')
          maybeInvoke(latestCallbacksRef.current?.['update-user-video'], [
            localAudioTrackRef.current,
            nextTrack,
          ])
          return nextTrack
        })
        .catch((error) => {
          recordDeviceError('camera', error)
          throw error
        })
        .finally(() => {
          creatingVideoTrackRef.current = null
        })
    }

    return creatingVideoTrackRef.current
  }, [
    acquireLocalTrackWithRetry,
    clearDeviceError,
    latestCallbacksRef,
    latestRtcPropsRef,
    recordDeviceError,
    selectedDevices.cameraDeviceId,
  ])

  const ensureAudioTrack = React.useCallback(async () => {
    if (localAudioTrackRef.current) {
      return localAudioTrackRef.current
    }

    if (!creatingAudioTrackRef.current) {
      const preferredMicrophoneId = selectedDevices.microphoneDeviceId

      creatingAudioTrackRef.current = acquireLocalTrackWithRetry(
        'microphone',
        (useDefaultDevice) =>
          AgoraRTC.createMicrophoneAudioTrack(
            preferredMicrophoneId && !useDefaultDevice
              ? { microphoneId: preferredMicrophoneId }
              : undefined,
          ),
        Boolean(preferredMicrophoneId),
      )
        .then((nextTrack) => {
          createdAudioTrackRef.current = true
          setLocalAudioTrack(nextTrack)
          localAudioTrackRef.current = nextTrack
          clearDeviceError('microphone')
          // If the OS/driver kills this mic mid-call (USB unplug, driver crash, sleep), the
          // published track silently goes dead and the far side stops hearing us. Recreate +
          // republish so audio self-heals. recoverLocalAudioRef is wired up further below.
          try {
            nextTrack.on?.('track-ended', () => recoverLocalAudioRef.current?.(nextTrack))
          } catch (error) {
            console.warn('[AgoraRtcSurface] Failed to attach mic track-ended recovery', error)
          }
          return nextTrack
        })
        .catch((error) => {
          recordDeviceError('microphone', error)
          throw error
        })
        .finally(() => {
          creatingAudioTrackRef.current = null
        })
    }

    return creatingAudioTrackRef.current
  }, [
    acquireLocalTrackWithRetry,
    clearDeviceError,
    recordDeviceError,
    selectedDevices.microphoneDeviceId,
  ])

  const syncLocalTracks = React.useCallback(async (activeClient = null) => {
    const runSync = async () => {
      const currentClient = activeClient || clientRef.current
      const currentRtcProps = latestRtcPropsRef.current

      if (!currentClient || !joinedRef.current || currentRtcProps.role === 'audience') {
        return
      }

      if (currentRtcProps.enableDualStream) {
        try {
          await currentClient.enableDualStream()
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to enable dual stream', error)
        }

        const lowStreamParameter = buildAgoraLowStreamParameter(currentRtcProps.videoQuality)
        if (lowStreamParameter) {
          try {
            await currentClient.setLowStreamParameter?.(lowStreamParameter)
          } catch (error) {
            console.warn('[AgoraRtcSurface] Failed to set low stream parameters', error)
          }
        }

        try {
          await currentClient.setRemoteDefaultVideoStreamType?.(
            getAgoraRemoteStreamType(currentRtcProps.videoQuality),
          )
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to set default remote stream type', error)
        }
      }

      let nextVideoTrack = localVideoTrackRef.current
      let nextAudioTrack = localAudioTrackRef.current

      // Acquire/publish video and audio independently: a camera failure must never stop the
      // mic from coming up (and vice versa). Each branch swallows its own error so the join
      // still completes and the user gets an actionable banner for whichever device failed.
      if (localMediaIntent.video && !nextVideoTrack) {
        try {
          nextVideoTrack = await ensureVideoTrack()
          if (
            !joinedRef.current ||
            destroyedRef.current ||
            currentClient !== (activeClient || clientRef.current)
          ) {
            return
          }
          await currentClient.publish([nextVideoTrack])
        } catch (error) {
          nextVideoTrack = localVideoTrackRef.current || null
          console.warn('[AgoraRtcSurface] Failed to acquire or publish local video', error)
        }
      }

      if (localMediaIntent.audio && !nextAudioTrack) {
        try {
          nextAudioTrack = await ensureAudioTrack()
          if (
            !joinedRef.current ||
            destroyedRef.current ||
            currentClient !== (activeClient || clientRef.current)
          ) {
            return
          }
          await currentClient.publish([nextAudioTrack])
        } catch (error) {
          nextAudioTrack = localAudioTrackRef.current || null
          console.warn('[AgoraRtcSurface] Failed to acquire or publish local audio', error)
        }
      }

      if (nextVideoTrack && selectedDevices.cameraDeviceId) {
        try {
          await nextVideoTrack.setDevice(selectedDevices.cameraDeviceId)
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to switch camera device', error)
        }
      }

      if (nextVideoTrack && typeof nextVideoTrack.setEncoderConfiguration === 'function') {
        const encoderConfig = buildAgoraEncoderConfig(currentRtcProps.videoQuality)

        if (encoderConfig) {
          try {
            await nextVideoTrack.setEncoderConfiguration(encoderConfig)
          } catch (error) {
            console.warn('[AgoraRtcSurface] Failed to apply video encoder configuration', error)
          }
        }
      }

      if (nextAudioTrack && selectedDevices.microphoneDeviceId) {
        try {
          await nextAudioTrack.setDevice(selectedDevices.microphoneDeviceId)
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to switch microphone device', error)
        }
      }

      if (nextVideoTrack) {
        try {
          await nextVideoTrack.setEnabled(localMediaIntent.video)
          setLocalPreviewBindingToken((previousToken) => previousToken + 1)
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to sync local video state', error)
        }
      }

      if (nextAudioTrack) {
        try {
          await nextAudioTrack.setEnabled(localMediaIntent.audio)
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to sync local audio state', error)
        }
      }

      await syncPlaybackDevice()
    }

    const nextSync = localTrackSyncPromiseRef.current.catch(() => {}).then(runSync)
    localTrackSyncPromiseRef.current = nextSync
    return nextSync
  }, [
    ensureAudioTrack,
    ensureVideoTrack,
    latestRtcPropsRef,
    localMediaIntent.audio,
    localMediaIntent.video,
    selectedDevices.cameraDeviceId,
    selectedDevices.microphoneDeviceId,
    syncPlaybackDevice,
  ])
  const syncLocalTracksRef = useLatestRef(syncLocalTracks)

  // Re-acquire the local microphone: tear down the current (possibly dead) mic track and
  // recreate + republish it via the normal sync path. Used both by the auto-recovery when a
  // track ends and by the manual "Restore audio" action. Ref-based throughout (no stale state).
  const reacquireLocalMicrophone = React.useCallback(
    async (deadTrack = null) => {
      if (destroyedRef.current) {
        return
      }
      const trackToDrop = deadTrack || localAudioTrackRef.current
      const currentClient = clientRef.current
      if (currentClient && trackToDrop) {
        try {
          await currentClient.unpublish?.([trackToDrop])
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to unpublish mic during re-acquire', error)
        }
      }
      if (trackToDrop) {
        try {
          trackToDrop.close?.()
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to close mic during re-acquire', error)
        }
      }
      if (localAudioTrackRef.current === trackToDrop) {
        localAudioTrackRef.current = null
      }
      createdAudioTrackRef.current = false
      creatingAudioTrackRef.current = null
      setLocalAudioTrack(null)
      if (joinedRef.current && !destroyedRef.current) {
        try {
          await syncLocalTracksRef.current?.(clientRef.current)
        } catch (error) {
          console.warn('[AgoraRtcSurface] Failed to re-publish mic during re-acquire', error)
        }
      }
    },
    [syncLocalTracksRef],
  )

  React.useEffect(() => {
    recoverLocalAudioRef.current = (deadTrack) => {
      const now = Date.now()
      // Debounce so a permanently-flapping device can't spin in a tight recreate loop.
      if (now - lastMicRecoveryAtRef.current < 3000) {
        return
      }
      lastMicRecoveryAtRef.current = now
      reacquireLocalMicrophone(deadTrack).catch(() => {})
    }
    return () => {
      recoverLocalAudioRef.current = null
    }
  }, [reacquireLocalMicrophone])

  // Full audio recovery for the manual "Restore audio" control: re-kick remote playback AND
  // re-acquire the local mic — the same belt-and-braces reset operators do by hand.
  const restoreAudioPipeline = React.useCallback(async () => {
    recoverRemoteAudioPlayback({ force: true })
    await reacquireLocalMicrophone()
  }, [recoverRemoteAudioPlayback, reacquireLocalMicrophone])
  const restoreAudioPipelineRef = useLatestRef(restoreAudioPipeline)

  // Manual "Try again" from the device-error banner: clear the recorded errors and re-run the
  // normal track-sync path so acquisition (with retry + default-device fallback) is attempted
  // afresh — e.g. after the user closed the app hogging the camera or fixed permissions.
  const retryLocalMedia = React.useCallback(async () => {
    setDeviceError({ camera: null, microphone: null })

    if (!joinedRef.current || destroyedRef.current) {
      return
    }

    try {
      await syncLocalTracksRef.current?.(clientRef.current)
    } catch (error) {
      console.warn('[AgoraRtcSurface] Manual media retry failed', error)
    }
  }, [syncLocalTracksRef])

  const cleanupScreenShare = React.useCallback(async () => {
    const currentScreenTrack = screenTrackRef.current
    const currentScreenClient = screenClientRef.current
    const currentRtcClient = clientRef.current

    screenTrackRef.current = null
    screenClientRef.current = null
    setScreenShareActive(false)

    if (currentScreenTrack) {
      try {
        currentScreenTrack.close()
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to close screen track', error)
      }
    }

    if (currentScreenClient) {
      try {
        await currentScreenClient.leave()
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to leave screen share client', error)
      }
    } else if (currentScreenTrack && currentRtcClient && localVideoTrackRef.current) {
      try {
        await currentRtcClient.unpublish([currentScreenTrack].filter(Boolean))
        await currentRtcClient.publish([localVideoTrackRef.current])
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to restore local video track after screen share', error)
      }
    }
  }, [])

  const stopRtcSession = React.useCallback(async (activeClient) => {
    const currentClient = activeClient || clientRef.current
    const wasJoined = joinedRef.current

    joinedRef.current = false
    setRtcReady(false)
    setJoiningRtc(false)

    await cleanupScreenShare()

    if (currentClient) {
      try {
        currentClient.removeAllListeners?.()
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to remove client listeners', error)
      }
    }

    if (localAudioTrackRef.current) {
      try {
        await currentClient?.unpublish?.([localAudioTrackRef.current])
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to unpublish local audio track', error)
      }
    }

    if (localVideoTrackRef.current) {
      try {
        await currentClient?.unpublish?.([localVideoTrackRef.current])
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to unpublish local video track', error)
      }
    }

    if (createdAudioTrackRef.current && localAudioTrackRef.current) {
      try {
        localAudioTrackRef.current.close()
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to close local audio track', error)
      }
    }

    if (createdVideoTrackRef.current && localVideoTrackRef.current) {
      try {
        localVideoTrackRef.current.close()
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to close local video track', error)
      }
    }

    createdAudioTrackRef.current = false
    createdVideoTrackRef.current = false
    creatingAudioTrackRef.current = null
    creatingVideoTrackRef.current = null
    setLocalAudioTrack(null)
    setLocalVideoTrack(null)
    localAudioTrackRef.current = null
    localVideoTrackRef.current = null
    setRemoteUsersMap({})
    setActiveSpeakerUid(null)
    setPinnedUid(null)

    if (currentClient && currentClient === clientRef.current) {
      try {
        await currentClient.leave()
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to leave RTC channel', error)
      }
    }

    if (clientRef.current === currentClient) {
      clientRef.current = null
    }

    if (wasJoined) {
      maybeInvoke(
        latestCallbacksRef.current?.['connection-state-change'],
        'left',
        'joined',
        'local-leave',
      )
    }
  }, [cleanupScreenShare])

  const stopRtmSession = React.useCallback(async () => {
    const currentRtmClient = rtmClientRef.current

    rtmClientRef.current = null
    rtmConnectedRef.current = false
    setUidMap({})
    setUsernameMap({})
    setPresenceStateByUid({})
    reverseUidMapRef.current = {}
    stableIdentityToRtcUidRef.current = {}
    rtcUidToStableIdentityRef.current = {}
    presenceIdentityByUserIdRef.current = {}

    if (!currentRtmClient) {
      return
    }

    try {
      await currentRtmClient.presence.removeState(
        latestRtcPropsRef.current.channel,
        RTM_CHANNEL_TYPE,
      )
    } catch (error) {
      console.warn('[AgoraRtcSurface] Failed to remove RTM presence state', error)
    }

    try {
      await currentRtmClient.unsubscribe(latestRtcPropsRef.current.channel)
    } catch (error) {
      console.warn('[AgoraRtcSurface] Failed to unsubscribe RTM channel', error)
    }

    try {
      await currentRtmClient.logout()
    } catch (error) {
      console.warn('[AgoraRtcSurface] Failed to logout RTM client', error)
    }

    try {
      currentRtmClient.removeAllListeners?.()
    } catch (error) {
      console.warn('[AgoraRtcSurface] Failed to remove RTM listeners', error)
    }
  }, [latestRtcPropsRef])

  React.useEffect(() => {
    destroyedRef.current = false

    return () => {
      destroyedRef.current = true
    }
  }, [])

  React.useEffect(() => {
    if (!settingsAnchorEl) {
      return
    }

    refreshDevices().catch(() => {})
  }, [refreshDevices, settingsAnchorEl])

  React.useEffect(() => {
    refreshDevices().catch(() => {})

    const mediaDevices = navigator?.mediaDevices

    if (!mediaDevices?.addEventListener) {
      return undefined
    }

    const handleDeviceChange = () => {
      refreshDevices().catch(() => {})
    }

    mediaDevices.addEventListener('devicechange', handleDeviceChange)

    return () => {
      mediaDevices.removeEventListener('devicechange', handleDeviceChange)
    }
  }, [refreshDevices])

  React.useEffect(() => {
    if (!resolvedRtcProps.appId || !resolvedRtcProps.channel || !resolvedRtcProps.callActive) {
      return undefined
    }

    let cancelled = false
    const rtcClient =
      resolvedRtcProps.customRtcClient ||
      AgoraRTC.createClient({
        codec: 'h264',
        mode: 'rtc',
      })

    clientRef.current = rtcClient

    const handleConnectionStateChange = (curState, prevState, reason) => {
      maybeInvoke(
        latestCallbacksRef.current?.['connection-state-change'],
        curState,
        prevState,
        reason,
      )
    }

    const handleUserJoined = (user) => {
      if (isIgnoredRemoteRtcUid(user?.uid, latestRtcPropsRef.current.uid)) {
        removeRemoteUser(user?.uid)
        return
      }

      applyRemoteParticipantEvent({
        type: AGORA_REMOTE_PARTICIPANT_EVENT_TYPES.joined,
        user,
        occurredAt: Date.now(),
      })
      maybeInvoke(latestCallbacksRef.current?.['user-joined'], user)
    }

    const handleUserLeft = (user, reason) => {
      if (isIgnoredRemoteRtcUid(user?.uid, latestRtcPropsRef.current.uid)) {
        removeRemoteUser(user?.uid)
        return
      }

      applyRemoteParticipantEvent({
        type: AGORA_REMOTE_PARTICIPANT_EVENT_TYPES.left,
        user,
      })
      maybeInvoke(latestCallbacksRef.current?.['user-left'], user, reason)
    }

    const handleUserPublished = async (user, mediaType) => {
      if (isIgnoredRemoteRtcUid(user?.uid, latestRtcPropsRef.current.uid)) {
        removeRemoteUser(user?.uid)
        return
      }

      try {
        await rtcClient.subscribe(user, mediaType)

        if (mediaType === 'video' && latestRtcPropsRef.current.enableDualStream) {
          try {
            await rtcClient.setRemoteVideoStreamType?.(
              user.uid,
              getAgoraRemoteStreamType(latestRtcPropsRef.current.videoQuality),
            )
          } catch (error) {
            console.warn('[AgoraRtcSurface] Failed to set remote video stream type', error)
          }

          try {
            await rtcClient.setStreamFallbackOption?.(user.uid, 1)
          } catch (error) {
            console.warn('[AgoraRtcSurface] Failed to set remote stream fallback option', error)
          }
        }

        if (mediaType === 'audio' && user.audioTrack) {
          user.audioTrack.play()
          await applyPlaybackDeviceToTrack(
            user.audioTrack,
            selectedDevices.playbackDeviceId || resolvedRtcProps.playbackDeviceId,
          )
        }

        applyRemoteParticipantEvent({
          type: AGORA_REMOTE_PARTICIPANT_EVENT_TYPES.published,
          user,
          mediaType,
          screenshareUid: resolvedRtcProps.screenshareUid,
          occurredAt: Date.now(),
        })

        maybeInvoke(latestCallbacksRef.current?.['user-published'], user, mediaType)
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to subscribe to remote user', error)
      }
    }

    const handleUserUnpublished = (user, mediaType) => {
      if (isIgnoredRemoteRtcUid(user?.uid, latestRtcPropsRef.current.uid)) {
        removeRemoteUser(user?.uid)
        return
      }

      applyRemoteParticipantEvent({
        type: AGORA_REMOTE_PARTICIPANT_EVENT_TYPES.unpublished,
        user,
        mediaType,
        screenshareUid: resolvedRtcProps.screenshareUid,
        occurredAt: Date.now(),
      })

      const userDescriptor = createRtcUserDescriptor({
        uid: user.uid,
        hasAudio: mediaType === 'audio' ? false : Boolean(user.audioTrack),
        hasVideo: mediaType === 'video' ? false : Boolean(user.videoTrack),
        isLocal: false,
      })

      if (mediaType === 'audio') {
        maybeInvoke(
          latestCallbacksRef.current?.['remote-user-mute-audio'],
          userDescriptor,
          REMOTE_TRACK_STATES.no,
        )
      }

      if (mediaType === 'video') {
        maybeInvoke(
          latestCallbacksRef.current?.['remote-user-mute-video'],
          userDescriptor,
          REMOTE_TRACK_STATES.no,
        )
      }

      maybeInvoke(latestCallbacksRef.current?.['user-unpublished'], user, mediaType)
    }

    const handleVolumeIndicator = (levels) => {
      maybeInvoke(latestCallbacksRef.current?.['volume-indicator'], levels)

      if (!latestRtcPropsRef.current.activeSpeaker || latestRtcPropsRef.current.layout !== AGORA_LAYOUTS.pin) {
        return
      }

      const activeSpeaker = [...levels]
        .filter((item) => item?.uid !== undefined)
        .sort((left, right) => (right?.level || 0) - (left?.level || 0))[0]

      if (activeSpeaker?.uid === undefined) {
        return
      }

      setActiveSpeakerUid(activeSpeaker.uid)
      maybeInvoke(latestCallbacksRef.current?.ActiveSpeaker, activeSpeaker.uid)
    }

    const handleUserInfoUpdated = (uid, msg) => {
      maybeInvoke(latestCallbacksRef.current?.['user-info-updated'], uid, msg)
    }

    rtcClient.on('connection-state-change', handleConnectionStateChange)
    rtcClient.on('user-joined', handleUserJoined)
    rtcClient.on('user-left', handleUserLeft)
    rtcClient.on('user-published', handleUserPublished)
    rtcClient.on('user-unpublished', handleUserUnpublished)
    rtcClient.on('user-info-updated', handleUserInfoUpdated)
    rtcClient.on('volume-indicator', handleVolumeIndicator)

    const registerOptionalClientEvent = (eventName) => {
      if (typeof rtcClient.on !== 'function') {
        return
      }

      rtcClient.on(eventName, (...args) => {
        maybeInvoke(latestCallbacksRef.current?.[eventName], ...args)
      })
    }

    ;[
      'media-reconnect-start',
      'media-reconnect-end',
      'stream-type-changed',
      'stream-fallback',
      'channel-media-relay-state',
      'channel-media-relay-event',
      'crypt-error',
      'token-privilege-will-expire',
      'token-privilege-did-expire',
      'network-quality',
    ].forEach(registerOptionalClientEvent)

    async function initializeRtc() {
      try {
        setJoiningRtc(true)
        maybeInvoke(
          latestCallbacksRef.current?.['connection-state-change'],
          'joining',
          'idle',
          'local-join-start',
        )

        if (resolvedRtcProps.enableDualStream) {
          await rtcClient.enableDualStream()

          const lowStreamParameter = buildAgoraLowStreamParameter(resolvedRtcProps.videoQuality)
          if (lowStreamParameter) {
            await rtcClient.setLowStreamParameter?.(lowStreamParameter)
          }

          await rtcClient.setRemoteDefaultVideoStreamType?.(
            getAgoraRemoteStreamType(resolvedRtcProps.videoQuality),
          )
        }

        if (resolvedRtcProps.activeSpeaker) {
          rtcClient.enableAudioVolumeIndicator?.()
        }

        await rtcClient.join(
          resolvedRtcProps.appId,
          resolvedRtcProps.channel,
          resolvedRtcProps.token || null,
          resolvedRtcProps.uid ?? null,
        )

        if (cancelled || destroyedRef.current) {
          return
        }

        joinedRef.current = true
        await syncLocalTracksRef.current(rtcClient)
        if (cancelled || destroyedRef.current || !joinedRef.current) {
          return
        }
        setRtcReady(true)
        maybeInvoke(
          latestCallbacksRef.current?.['connection-state-change'],
          'joined',
          'joining',
          'local-join-success',
        )
      } catch (error) {
        console.error('[AgoraRtcSurface] Failed to initialize RTC session', error)
        maybeInvoke(
          latestCallbacksRef.current?.['connection-state-change'],
          'failed',
          'joining',
          error?.message || 'local-join-failed',
        )
      } finally {
        if (!cancelled) {
          setJoiningRtc(false)
        }
      }
    }

    initializeRtc().catch(() => {})

    return () => {
      cancelled = true
      stopRtcSession(rtcClient).catch(() => {})
    }
  }, [
    latestCallbacksRef,
    latestRtcPropsRef,
    applyRemoteParticipantEvent,
    removeRemoteUser,
    resolvedRtcProps.appId,
    resolvedRtcProps.callActive,
    resolvedRtcProps.channel,
    resolvedRtcProps.customRtcClient,
    resolvedRtcProps.enableDualStream,
    resolvedRtcProps.token,
    resolvedRtcProps.uid,
    stopRtcSession,
  ])

  React.useEffect(() => {
    if (!rtcReady) {
      return
    }

    syncLocalTracks().catch(() => {})
  }, [
    localMediaIntent.audio,
    localMediaIntent.video,
    rtcReady,
    selectedDevices,
    syncLocalTracks,
  ])

  React.useEffect(() => {
    syncPlaybackDevice().catch(() => {})
  }, [syncPlaybackDevice])

  React.useEffect(() => {
    if (
      resolvedRtcProps.disableRtm ||
      !resolvedRtcProps.appId ||
      !resolvedRtcProps.channel ||
      !resolvedRtcProps.callActive
    ) {
      return undefined
    }

    const currentRtmUserId = stringifyUid(rtmProps?.uid || resolvedRtcProps.uid || Date.now())
    const rtmBarrierKey = buildRtmBarrierKey(resolvedRtcProps.appId, currentRtmUserId)
    let cancelled = false
    const nextRtmClient = new AgoraRTM.RTM(resolvedRtcProps.appId, currentRtmUserId)
    rtmClientRef.current = nextRtmClient

    const registerExternalRtmCallbacks = () => {
      const resolvedRtmCallbacks = latestRtmCallbacksRef.current

      if (!isPlainObject(resolvedRtmCallbacks)) {
        return
      }

      const clientCallbacks = isPlainObject(resolvedRtmCallbacks.client)
        ? resolvedRtmCallbacks.client
        : resolvedRtmCallbacks

      Object.entries(clientCallbacks).forEach(([eventName, handler]) => {
        if (typeof handler === 'function') {
          nextRtmClient.addEventListener(eventName, handler)
        }
      })
    }

    registerExternalRtmCallbacks()

    const handleMessage = async (messageEvent) => {
      if (!messageEvent?.message || typeof messageEvent.message !== 'string') {
        return
      }

      try {
        const parsedMessage = JSON.parse(messageEvent.message)

        if (parsedMessage?.type === RTM_MESSAGE_TYPES.muteRequest) {
          const currentRtcUid = normalizeUid(latestRtcPropsRef.current.uid, null)
          const requestedRtcUid = normalizeUid(parsedMessage?.targetRtcUid, null)

          if (currentRtcUid === null || requestedRtcUid !== currentRtcUid) {
            return
          }

          if (parsedMessage.force === true) {
            if (parsedMessage.device === REMOTE_MUTING_DEVICES.camera && parsedMessage.mute) {
              setLocalMediaIntent((previousState) => ({ ...previousState, video: false }))
              maybeInvoke(
                latestCallbacksRef.current?.['local-user-mute-video'],
                TOGGLE_STATES.disabling,
              )
            }

            if (
              parsedMessage.device === REMOTE_MUTING_DEVICES.microphone &&
              parsedMessage.mute
            ) {
              setLocalMediaIntent((previousState) => ({ ...previousState, audio: false }))
              maybeInvoke(
                latestCallbacksRef.current?.['local-user-mute-audio'],
                TOGGLE_STATES.disabling,
              )
            }
          } else {
            setPendingMuteRequest({
              device: parsedMessage.device,
              mute: parsedMessage.mute,
            })
          }
        }
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to parse RTM message', error)
      }
    }

    const handlePresence = async (presenceEvent) => {
      if (
        presenceEvent?.channelName !== latestRtcPropsRef.current.channel ||
        presenceEvent?.channelType !== RTM_CHANNEL_TYPE
      ) {
        return
      }

      switch (presenceEvent.eventType) {
        case 'SNAPSHOT':
          ;(presenceEvent.snapshot || []).forEach((snapshotUser) => {
            updatePresenceParticipant(snapshotUser.userId, snapshotUser.states)
          })
          break
        case 'REMOTE_JOIN':
        case 'REMOTE_STATE_CHANGED':
          if (presenceEvent.publisher === currentRtmUserId) {
            return
          }

          if (presenceEvent.stateChanged && Object.keys(presenceEvent.stateChanged).length > 0) {
            updatePresenceParticipant(presenceEvent.publisher, presenceEvent.stateChanged)
          } else {
            try {
              const userState = await nextRtmClient.presence.getState(
                presenceEvent.publisher,
                latestRtcPropsRef.current.channel,
                RTM_CHANNEL_TYPE,
              )
              updatePresenceParticipant(userState.userId, userState.states)
            } catch (error) {
              console.warn('[AgoraRtcSurface] Failed to resolve RTM user state', error)
            }
          }
          break
        case 'REMOTE_LEAVE':
        case 'REMOTE_TIMEOUT':
          removePresenceParticipant(presenceEvent.publisher)
          break
        default:
          break
      }
    }

    const handleTokenPrivilegeWillExpire = () => {
      maybeInvoke(latestRtmCallbacksRef.current?.tokenPrivilegeWillExpire)
    }

    nextRtmClient.addEventListener('message', handleMessage)
    nextRtmClient.addEventListener('presence', handlePresence)
    nextRtmClient.addEventListener('tokenPrivilegeWillExpire', handleTokenPrivilegeWillExpire)

    async function initializeRtm() {
      try {
        // Wait out any in-flight logout of the same uid (from a just-unmounted instance) before we
        // log in, so the new login doesn't collide with it -> SAME_UID_LOGIN (-10023). Bounded so a
        // hung logout can't wedge the new session.
        const pendingLogout = pendingRtmLogoutByKey.get(rtmBarrierKey)
        if (pendingLogout) {
          await Promise.race([pendingLogout, rtmBarrierDelay(RTM_LOGOUT_BARRIER_TIMEOUT_MS)])
          if (cancelled || destroyedRef.current) {
            return
          }
        }

        try {
          await nextRtmClient.login({
            token: rtmProps?.token || undefined,
          })
        } catch (loginError) {
          if (!isDuplicateRtmLoginError(loginError) || cancelled || destroyedRef.current) {
            throw loginError
          }
          // Duplicate-login race we couldn't fully serialize: force this client out, back off,
          // and retry the login once.
          try {
            await nextRtmClient.logout()
          } catch (logoutError) {
            console.warn('[AgoraRtcSurface] Failed to logout before RTM login retry', logoutError)
          }
          await rtmBarrierDelay(600)
          if (cancelled || destroyedRef.current) {
            return
          }
          await nextRtmClient.login({
            token: rtmProps?.token || undefined,
          })
        }

        if (cancelled || destroyedRef.current) {
          return
        }

        await nextRtmClient.subscribe(resolvedRtcProps.channel, {
          withMessage: true,
          withPresence: true,
          beQuiet: false,
        })

        if (cancelled || destroyedRef.current) {
          return
        }

        rtmConnectedRef.current = true

        const snapshotResponse = await nextRtmClient.presence.whoNow(
          resolvedRtcProps.channel,
          RTM_CHANNEL_TYPE,
          {
            includedUserId: true,
            includedState: true,
          },
        )

        if (cancelled || destroyedRef.current) {
          return
        }

        ;(snapshotResponse?.occupants || []).forEach((occupant) => {
          updatePresenceParticipant(occupant.userId, occupant.states)
        })

        await updateLocalPresence()
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to initialize RTM session', error)
      }
    }

    initializeRtm().catch(() => {})

    return () => {
      cancelled = true
      // Publish this logout as the barrier for our uid so a remount with the same uid waits for it
      // to settle before logging in again. Self-clears once it settles (unless already superseded).
      const teardownPromise = stopRtmSession().catch(() => {})
      pendingRtmLogoutByKey.set(rtmBarrierKey, teardownPromise)
      teardownPromise.finally(() => {
        if (pendingRtmLogoutByKey.get(rtmBarrierKey) === teardownPromise) {
          pendingRtmLogoutByKey.delete(rtmBarrierKey)
        }
      })
    }
  }, [
    latestCallbacksRef,
    latestRtmCallbacksRef,
    removePresenceParticipant,
    resolvedRtcProps.appId,
    resolvedRtcProps.callActive,
    resolvedRtcProps.channel,
    resolvedRtcProps.disableRtm,
    resolvedRtcProps.uid,
    rtmProps?.token,
    rtmProps?.uid,
    stopRtmSession,
    updateLocalPresence,
    updatePresenceParticipant,
  ])

  React.useEffect(() => {
    if (!rtmConnectedRef.current || resolvedRtcProps.disableRtm) {
      return
    }

    updateLocalPresence().catch(() => {})
  }, [
    resolvedRtcProps.disableRtm,
    resolvedRtcProps.role,
    resolvedRtcProps.uid,
    rtmProps?.username,
    updateLocalPresence,
  ])

  const handleSelectDevice = React.useCallback((key, value) => {
    setSelectedDevices((previousState) => ({
      ...previousState,
      [key]: value,
    }))
  }, [])

  const handleToggleAudio = React.useCallback(async () => {
    const nextEnabled = !localMediaIntent.audio

    maybeInvoke(
      latestCallbacksRef.current?.['local-user-mute-audio'],
      nextEnabled ? TOGGLE_STATES.enabling : TOGGLE_STATES.disabling,
    )

    setLocalMediaIntent((previousState) => ({
      ...previousState,
      audio: nextEnabled,
    }))

    maybeInvoke(
      latestCallbacksRef.current?.['local-user-mute-audio'],
      nextEnabled ? TOGGLE_STATES.enabled : TOGGLE_STATES.disabled,
    )
  }, [latestCallbacksRef, localMediaIntent.audio])

  const handleToggleVideo = React.useCallback(async () => {
    const nextEnabled = !localMediaIntent.video

    maybeInvoke(
      latestCallbacksRef.current?.['local-user-mute-video'],
      nextEnabled ? TOGGLE_STATES.enabling : TOGGLE_STATES.disabling,
    )

    setLocalMediaIntent((previousState) => ({
      ...previousState,
      video: nextEnabled,
    }))

    maybeInvoke(
      latestCallbacksRef.current?.['local-user-mute-video'],
      nextEnabled ? TOGGLE_STATES.enabled : TOGGLE_STATES.disabled,
    )
  }, [latestCallbacksRef, localMediaIntent.video])

  const handleEndCall = React.useCallback(async () => {
    await stopRtmSession()
    await stopRtcSession()
    maybeInvoke(latestCallbacksRef.current?.['leave-channel'])
    maybeInvoke(latestCallbacksRef.current?.EndCall)
  }, [latestCallbacksRef, stopRtcSession, stopRtmSession])

  const handleStopScreenShare = React.useCallback(async () => {
    await cleanupScreenShare()
    maybeInvoke(latestCallbacksRef.current?.['screen-share-state-changed'], false)
    maybeInvoke(latestCallbacksRef.current?.Screensharing, false)
  }, [cleanupScreenShare, latestCallbacksRef])

  const handleStartScreenShare = React.useCallback(async () => {
    if (screenShareActive) {
      await handleStopScreenShare()
      return
    }

    const currentRtcProps = latestRtcPropsRef.current

    if (!joinedRef.current) {
      throw new Error('[AgoraRtcSurface] Cannot start screen share before local join completes')
    }

    try {
      const screenTrack = await AgoraRTC.createScreenVideoTrack()
      screenTrackRef.current = Array.isArray(screenTrack) ? screenTrack[0] : screenTrack

      if (!currentRtcProps.screenshareUid) {
        const existingVideoTrack = await ensureVideoTrack()
        await clientRef.current.unpublish([existingVideoTrack])
        await clientRef.current.publish([screenTrackRef.current])
      } else {
        const screenClient = AgoraRTC.createClient({ codec: 'h264', mode: 'rtc' })
        screenClientRef.current = screenClient
        await screenClient.join(
          currentRtcProps.appId,
          currentRtcProps.channel,
          currentRtcProps.screenshareToken || null,
          currentRtcProps.screenshareUid,
        )
        await screenClient.publish([screenTrackRef.current])
      }

      screenTrackRef.current.on('track-ended', () => {
        handleStopScreenShare().catch(() => {})
      })

      setScreenShareActive(true)
      maybeInvoke(latestCallbacksRef.current?.['screen-share-state-changed'], true)
      maybeInvoke(latestCallbacksRef.current?.Screensharing, true)
    } catch (error) {
      console.warn('[AgoraRtcSurface] Failed to start screen share', error)
      await handleStopScreenShare()
      throw error
    }
  }, [
    cleanupScreenShare,
    ensureVideoTrack,
    handleStopScreenShare,
    latestCallbacksRef,
    latestRtcPropsRef,
    screenShareActive,
  ])
  const handleStartScreenShareRef = useLatestRef(handleStartScreenShare)
  const handleStopScreenShareRef = useLatestRef(handleStopScreenShare)
  const registerRuntimeCommandsCallbackRef = useLatestRef(
    callbacks?.['register-runtime-commands'],
  )
  React.useEffect(() => {
    maybeInvoke(registerRuntimeCommandsCallbackRef.current, {
      startScreenShare: () => handleStartScreenShareRef.current?.(),
      stopScreenShare: () => handleStopScreenShareRef.current?.(),
      restoreAudio: () => restoreAudioPipelineRef.current?.(),
    })

    return () => {
      maybeInvoke(registerRuntimeCommandsCallbackRef.current, null)
    }
  }, [
    registerRuntimeCommandsCallbackRef,
    handleStartScreenShareRef,
    handleStopScreenShareRef,
    restoreAudioPipelineRef,
    resolvedRtcProps.appId,
    resolvedRtcProps.channel,
    resolvedRtcProps.uid,
  ])

  const handleRemoteMuteRequest = React.useCallback(
    async (rtcUid, device, mute) => {
      const currentRtmClient = rtmClientRef.current
      const targetPeerId =
        uidMapRef.current[rtcUid] || uidMapRef.current[String(rtcUid)] || reverseUidMapRef.current[rtcUid]

      if (!currentRtmClient || !targetPeerId) {
        return
      }

      try {
        await currentRtmClient.publish(
          String(targetPeerId),
          JSON.stringify({
            type: RTM_MESSAGE_TYPES.muteRequest,
            targetRtcUid: rtcUid,
            device,
            mute,
            force: latestRtmPropsRef.current?.showPopUpBeforeRemoteMute === false,
          }),
          {
            channelType: 'USER',
          },
        )
      } catch (error) {
        console.warn('[AgoraRtcSurface] Failed to publish remote mute request', error)
      }
    },
    [latestRtmPropsRef],
  )

  const handleAcceptRemoteMuteRequest = React.useCallback(() => {
    if (!pendingMuteRequest) {
      return
    }

    if (pendingMuteRequest.device === REMOTE_MUTING_DEVICES.camera) {
      setLocalMediaIntent((previousState) => ({
        ...previousState,
        video: !pendingMuteRequest.mute,
      }))
      maybeInvoke(
        latestCallbacksRef.current?.['local-user-mute-video'],
        pendingMuteRequest.mute ? TOGGLE_STATES.disabling : TOGGLE_STATES.enabling,
      )
    }

    if (pendingMuteRequest.device === REMOTE_MUTING_DEVICES.microphone) {
      setLocalMediaIntent((previousState) => ({
        ...previousState,
        audio: !pendingMuteRequest.mute,
      }))
      maybeInvoke(
        latestCallbacksRef.current?.['local-user-mute-audio'],
        pendingMuteRequest.mute ? TOGGLE_STATES.disabling : TOGGLE_STATES.enabling,
      )
    }

    setPendingMuteRequest(null)
  }, [latestCallbacksRef, pendingMuteRequest])

  const currentLocalUsername =
    rtmProps?.username || resolveUsernameForUid(usernameMap, resolvedRtcProps.uid) || ''
  const defaultLocalParticipantRole = 'participant'

  const rawRemoteUsers = React.useMemo(
    () =>
      Object.values(remoteUsersMap)
        .filter((remoteUser) => {
          if (remoteUser?.uid === undefined) {
            return false
          }

          return !isIgnoredRemoteRtcUid(remoteUser.uid, resolvedRtcProps.uid)
        })
        .sort((left, right) => Number(left.uid) - Number(right.uid)),
    [remoteUsersMap, resolvedRtcProps.uid],
  )

  const dedupedRemoteUsers = React.useMemo(() => {
    const participantsByIdentity = new Map()
    const passthroughUsers = []

    rawRemoteUsers.forEach((remoteUser) => {
      const metadata = resolveUserMetadataForUid({
        usernameMap,
        uid: remoteUser.uid,
        fallbackUsername: '',
      })
      const stableIdentityKey = metadata.identityKey

      if (!stableIdentityKey) {
        passthroughUsers.push(remoteUser)
        return
      }

      const existingCandidate = participantsByIdentity.get(stableIdentityKey)
      const preferredRtcUid = stableIdentityToRtcUidRef.current[stableIdentityKey]

      if (
        !existingCandidate ||
        shouldPreferRemoteUserCandidate(
          remoteUser,
          metadata,
          existingCandidate.remoteUser,
          existingCandidate.metadata,
          preferredRtcUid,
        )
      ) {
        participantsByIdentity.set(stableIdentityKey, {
          remoteUser,
          metadata,
        })
      }
    })

    return [...passthroughUsers, ...Array.from(participantsByIdentity.values()).map(
      ({ remoteUser }) => remoteUser,
    )].sort((left, right) => Number(left.uid) - Number(right.uid))
  }, [rawRemoteUsers, usernameMap])

  const allRemoteUsers = dedupedRemoteUsers

  const localUser = React.useMemo(
    () => ({
      uid: resolvedRtcProps.uid ?? 0,
      hasAudio: localMediaIntent.audio,
      hasVideo: screenShareActive
        ? Boolean(screenTrackRef.current || localVideoTrack)
        : localMediaIntent.video && Boolean(localVideoTrack),
      audioTrack: localAudioTrack,
      videoTrack:
        screenShareActive && !resolvedRtcProps.screenshareUid
          ? screenTrackRef.current || localVideoTrack
          : localVideoTrack,
      isScreenShare: Boolean(screenShareActive && !resolvedRtcProps.screenshareUid),
    }),
    [
      localAudioTrack,
      localMediaIntent.audio,
      localMediaIntent.video,
      localVideoTrack,
      resolvedRtcProps.uid,
      resolvedRtcProps.screenshareUid,
      screenShareActive,
    ],
  )
  const localScreenShareUser = React.useMemo(() => {
    if (
      !screenShareActive ||
      !screenTrackRef.current ||
      !resolvedRtcProps.screenshareUid
    ) {
      return null
    }

    return {
      uid: resolvedRtcProps.screenshareUid,
      hasAudio: false,
      hasVideo: true,
      audioTrack: null,
      videoTrack: screenTrackRef.current,
      isScreenShare: true,
      rawUsername: buildScreenShareUsername({
        username: currentLocalUsername,
        ownerUid: resolvedRtcProps.uid,
        fallbackDisplayName: 'You',
        fallbackRole: defaultLocalParticipantRole,
      }),
    }
  }, [
    currentLocalUsername,
    defaultLocalParticipantRole,
    resolvedRtcProps.screenshareUid,
    resolvedRtcProps.uid,
    screenShareActive,
  ])
  const dominantUser = React.useMemo(
    () =>
      resolveDominantUser({
        layout: resolvedRtcProps.layout,
        pinnedUid,
        activeSpeakerUid,
        localUser,
        remoteUsers: localScreenShareUser
          ? [localScreenShareUser, ...allRemoteUsers]
          : allRemoteUsers,
      }),
    [
      activeSpeakerUid,
      allRemoteUsers,
      localScreenShareUser,
      localUser,
      pinnedUid,
      resolvedRtcProps.layout,
    ],
  )

  const auxiliaryUsers = React.useMemo(() => {
    if (resolvedRtcProps.layout !== AGORA_LAYOUTS.pin || !dominantUser) {
      return []
    }

    return [...allRemoteUsers, localUser].filter(
      (user) => String(user.uid) !== String(dominantUser.uid),
    )
  }, [allRemoteUsers, dominantUser, localUser, resolvedRtcProps.layout])

  const participantSnapshot = React.useMemo(() => {
    const normalizeParticipant = (user, { isLocal = false } = {}) => {
      const fallbackUsername =
        user?.rawUsername || (isLocal ? currentLocalUsername : '')
      const metadata = resolveUserMetadataForUid({
        usernameMap,
        uid: user.uid,
        fallbackUsername,
      })
      const displayName =
        metadata.displayName ||
        getDisplayNameFromUsername(metadata.rawUsername) ||
        (isLocal ? 'You' : `Participant ${user.uid}`)

      return {
        uid: user.uid,
        isLocal,
        rawUsername: metadata.rawUsername || fallbackUsername || '',
        displayName,
        role: metadata.role || (isLocal ? defaultLocalParticipantRole : 'participant'),
        profilePicId: metadata.profilePicId || null,
        identityKey: metadata.identityKey || null,
        hasAudio: Boolean(
          user?.hasAudio === true ||
            user?.hasAudio === TOGGLE_STATES.enabled ||
            user?.audioTrack,
        ),
        hasVideo: Boolean(
          user?.hasVideo === true ||
            user?.hasVideo === TOGGLE_STATES.enabled ||
            user?.videoTrack,
        ),
        audioTrack: user?.audioTrack || null,
        videoTrack: user?.videoTrack || null,
        isScreenShare: Boolean(user?.isScreenShare),
      }
    }

    const localParticipant = normalizeParticipant(localUser, { isLocal: true })
    const remoteParticipants = [
      ...(localScreenShareUser ? [localScreenShareUser] : []),
      ...allRemoteUsers,
    ].map((remoteUser) => normalizeParticipant(remoteUser, { isLocal: false }))
    const nextUsernameMap = {}

    ;[localParticipant, ...remoteParticipants].forEach((participant) => {
      if (!participant.rawUsername) {
        return
      }

      getAllUidKeys(participant.uid).forEach((key) => {
        nextUsernameMap[key] = participant.rawUsername
      })
    })

    return {
      localParticipant,
      remoteParticipants,
      usernameMap: nextUsernameMap,
    }
  }, [
    allRemoteUsers,
    currentLocalUsername,
    defaultLocalParticipantRole,
    localScreenShareUser,
    localUser,
    usernameMap,
  ])

  React.useEffect(() => {
    maybeInvoke(latestCallbacksRef.current?.['participants-updated'], participantSnapshot)
  }, [latestCallbacksRef, participantSnapshot])

  const showRemoteMuteControls =
    resolvedRtcProps.disableRtm !== true &&
    latestRtmPropsRef.current?.isThisUserAllowedToMuteOthers === true

  const renderVideoCard = React.useCallback(
    (user, options = {}) => {
      const fallbackUsername =
        String(user.uid) === String(resolvedRtcProps.uid) ? currentLocalUsername : ''
      const userMetadata = resolveUserMetadataForUid({
        usernameMap,
        uid: user.uid,
        fallbackUsername,
      })
      const isLocalUser = String(user.uid) === String(resolvedRtcProps.uid)
      const displayUsername =
        userMetadata.displayName ||
        (isLocalUser ? 'You' : getDisplayNameFromUsername(userMetadata.rawUsername))
      const participantRole =
        userMetadata.role || (isLocalUser ? defaultLocalParticipantRole : 'participant')
      const participantRoleLabel = getParticipantRoleLabel(participantRole)

      return (
        <MemoVideoCard
          key={user.uid}
          user={user}
          isLocal={isLocalUser}
          isDominant={Boolean(options.isDominant)}
          username={resolvedRtcProps.disableRtm ? '' : displayUsername}
          localUsername={currentLocalUsername}
          participantRole={participantRole}
          hasRtmMetadata={Boolean(userMetadata.rawUsername)}
          videoTrack={user.videoTrack}
          renderMode={
            options.isDominant
              ? styleProps?.videoMode?.max || 'cover'
              : styleProps?.videoMode?.min || 'cover'
          }
          styleProps={styleProps}
          CustomVideoPlaceholder={resolvedRtcProps.CustomVideoPlaceholder}
          canRemoteMute={showRemoteMuteControls}
          presentationTone="default"
          participantRoleLabel={participantRoleLabel}
          showParticipantRoleBadge={false}
          useParticipantMetaOverlay={false}
          onRemoteMuteAudio={() =>
            handleRemoteMuteRequest(user.uid, REMOTE_MUTING_DEVICES.microphone, true)
          }
          onRemoteMuteVideo={() =>
            handleRemoteMuteRequest(user.uid, REMOTE_MUTING_DEVICES.camera, true)
          }
          onPin={() => {
            setPinnedUid(user.uid)
            maybeInvoke(
              latestCallbacksRef.current?.['user-swap'],
              createRtcUserDescriptor({
                uid: user.uid,
                hasAudio: user.hasAudio,
                hasVideo: user.hasVideo,
                isLocal: isLocalUser,
              }),
            )
          }}
          showPinControl={!isLocalUser}
          enableHoverInteractions={!isLocalUser}
          localPreviewBindingToken={isLocalUser ? localPreviewBindingToken : 0}
        />
      )
    },
    [
      currentLocalUsername,
      defaultLocalParticipantRole,
      handleRemoteMuteRequest,
      latestCallbacksRef,
      resolvedRtcProps.CustomVideoPlaceholder,
      resolvedRtcProps.disableRtm,
      resolvedRtcProps.uid,
      showRemoteMuteControls,
      styleProps,
      localPreviewBindingToken,
      usernameMap,
    ],
  )

  // Only surface an error for a device the user actually wants on (camera on = video intent).
  const deviceErrorNotice = React.useMemo(() => {
    const failing = []

    if (deviceError.camera && localMediaIntent.video) {
      failing.push({ kind: 'camera', reason: deviceError.camera.reason })
    }

    if (deviceError.microphone && localMediaIntent.audio) {
      failing.push({ kind: 'microphone', reason: deviceError.microphone.reason })
    }

    return buildDeviceErrorNotice(failing, {
      isMac: IS_MAC_PLATFORM,
      isStandalone: IS_STANDALONE_DISPLAY,
    })
  }, [deviceError, localMediaIntent.audio, localMediaIntent.video])

  // Portal to <body> so the banner is anchored to the viewport regardless of the host layout.
  // Every video surface (mentor /l/, /v/*, hotline, mentor chamber room, kiosk chamber screen)
  // renders this same AgoraRtcSurface, but some embed it inside transformed / overflow-hidden
  // containers where a bare position:fixed element would be clipped or mis-anchored.
  const deviceErrorOverlay =
    deviceErrorNotice && typeof document !== 'undefined' && document.body
      ? createPortal(
          <div style={deviceErrorOverlayStyle} data-role="agora-device-error">
            <div style={deviceErrorCardStyle}>
              <div style={deviceErrorIconWrapStyle}>
                <ErrorOutlineRoundedIcon sx={{ fontSize: 22, color: '#fca5a5' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={deviceErrorTitleStyle}>{deviceErrorNotice.title}</div>
                <div style={deviceErrorDetailStyle}>{deviceErrorNotice.detail}</div>
                <ul style={deviceErrorRemedyListStyle}>
                  {deviceErrorNotice.remedy.map((line, index) => (
                    <li key={index} style={deviceErrorRemedyItemStyle}>
                      {line}
                    </li>
                  ))}
                </ul>
                <div style={deviceErrorActionsStyle}>
                  {deviceErrorNotice.canRetry ? (
                    <button
                      type="button"
                      onClick={retryLocalMedia}
                      style={deviceErrorPrimaryButtonStyle}
                    >
                      <ReplayRoundedIcon sx={{ fontSize: 18 }} />
                      Try again
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setDeviceError({ camera: null, microphone: null })}
                    style={deviceErrorSecondaryButtonStyle}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setDeviceError({ camera: null, microphone: null })}
                style={deviceErrorCloseButtonStyle}
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
          </div>,
          document.body,
        )
      : null

  if (!resolvedRtcProps.appId || !resolvedRtcProps.channel) {
    return fallback
  }

  if (joiningRtc && !rtcReady && fallback) {
    return fallback
  }

  const localControls = (
    <div
      className="agora-rtc-localBtnContainer"
      style={{
        ...defaultLocalButtonContainerStyle,
        ...(styleProps?.localBtnContainer || {}),
      }}
    >
      {resolvedRtcProps.role !== 'audience' ? (
        <>
          <LocalControlButton
            title={localMediaIntent.video ? 'Turn camera off' : 'Turn camera on'}
            active={localMediaIntent.video}
            onClick={handleToggleVideo}
            dataRole="agora-toggle-video"
            style={mergeButtonStyles(styleProps, 'muteLocalVideo')}
          >
            {localMediaIntent.video ? (
              <VideocamRoundedIcon sx={{ fontSize: 24 }} />
            ) : (
              <VideocamOffRoundedIcon sx={{ fontSize: 24 }} />
            )}
          </LocalControlButton>
          <LocalControlButton
            title={localMediaIntent.audio ? 'Mute microphone' : 'Unmute microphone'}
            active={localMediaIntent.audio}
            onClick={handleToggleAudio}
            dataRole="agora-toggle-audio"
            style={mergeButtonStyles(styleProps, 'muteLocalAudio')}
          >
            {localMediaIntent.audio ? (
              <MicRoundedIcon sx={{ fontSize: 24 }} />
            ) : (
              <MicOffRoundedIcon sx={{ fontSize: 24 }} />
            )}
          </LocalControlButton>
          {resolvedRtcProps.enableScreensharing ? (
            <LocalControlButton
              title={screenShareActive ? 'Stop screen share' : 'Share screen'}
              active={screenShareActive}
              onClick={handleStartScreenShare}
              dataRole="agora-toggle-screenshare"
              style={mergeButtonStyles(styleProps, 'screenshare')}
            >
              {screenShareActive ? (
                <StopScreenShareRoundedIcon sx={{ fontSize: 24 }} />
              ) : (
                <ScreenShareRoundedIcon sx={{ fontSize: 24 }} />
              )}
            </LocalControlButton>
          ) : null}
        </>
      ) : null}
      <LocalControlButton
        title="Settings"
        active={Boolean(settingsAnchorEl)}
        onClick={(event) => {
          setSettingsAnchorEl((previousAnchorEl) =>
            previousAnchorEl ? null : event.currentTarget,
          )
        }}
        dataRole="agora-change-settings"
        style={mergeButtonStyles(styleProps, 'settings')}
      >
        <SettingsRoundedIcon sx={{ fontSize: 22 }} />
      </LocalControlButton>
      <LocalControlButton
        title="Leave call"
        active={false}
        onClick={handleEndCall}
        dataRole="agora-end-call"
        style={{
          ...endCallButtonStyle,
          ...(mergeButtonStyles(styleProps, 'endCall') || {}),
        }}
      >
        <CallEndRoundedIcon sx={{ fontSize: 22 }} />
      </LocalControlButton>
    </div>
  )

  const settingsDialogElement = (
    <SettingsPopover
      anchorEl={settingsAnchorEl}
      onClose={() => setSettingsAnchorEl(null)}
      deviceLists={deviceLists}
      selectedDevices={selectedDevices}
      onSelectDevice={handleSelectDevice}
      chamberKioskTheme={false}
    />
  )

  if (renderless) {
    return (
      <>
        {children}
        {deviceErrorOverlay}
      </>
    )
  }

  return (
    <div
      className={`agora-rtc-uikit-container agora-rtc-layout-${resolvedRtcProps.layout}`}
      style={{
        ...defaultUIKitContainerStyle,
        ...(styleProps?.UIKitContainer || {}),
      }}
    >
      {resolvedRtcProps.layout === AGORA_LAYOUTS.grid ? (
        <div
          style={{
            ...defaultGridContainerStyle,
            ...(styleProps?.gridVideoContainer || {}),
            gridTemplateColumns:
              allRemoteUsers.length + 1 > 9
                ? 'repeat(4, minmax(0, 1fr))'
                : allRemoteUsers.length + 1 > 4
                  ? 'repeat(3, minmax(0, 1fr))'
                  : allRemoteUsers.length + 1 > 1
                    ? 'repeat(2, minmax(0, 1fr))'
                    : 'minmax(0, 1fr)',
          }}
        >
          {[...allRemoteUsers, localUser].map((user) => (
            <div
              key={user.uid}
              style={{
                ...defaultGridCellStyle,
                ...(styleProps?.gridVideoCells || {}),
              }}
            >
              {renderVideoCard(user)}
            </div>
          ))}
        </div>
      ) : (
        <div
          className="agora-rtc-pinned-container"
          style={{
            ...defaultPinnedContainerStyle,
            ...(styleProps?.pinnedVideoContainer || {}),
          }}
        >
          <div
            className="agora-rtc-pinned-primary"
            style={{
              ...defaultPinnedPrimaryStyle,
              ...(styleProps?.maxViewContainer || {}),
            }}
          >
            {dominantUser ? renderVideoCard(dominantUser, { isDominant: true }) : null}
          </div>
          <div
            className="agora-rtc-pinned-secondary"
            style={{
              ...defaultPinnedSecondaryStyle,
              ...(styleProps?.scrollViewContainer || {}),
            }}
          >
            {auxiliaryUsers.map((user) => (
              <div
                key={user.uid}
                className="agora-rtc-pinned-secondary-cell"
                data-card-uid={user.uid}
                data-local-user={
                  String(user.uid) === String(resolvedRtcProps.uid) ? 'true' : 'false'
                }
                style={{
                  ...defaultPinnedSecondaryCellStyle,
                  ...(styleProps?.minViewContainer || {}),
                }}
              >
                {renderVideoCard(user)}
              </div>
            ))}
          </div>
        </div>
      )}

      {localControls}
      {settingsDialogElement}
      {deviceErrorOverlay}
      <RemoteMuteDialog
        pendingMuteRequest={pendingMuteRequest}
        onAccept={handleAcceptRemoteMuteRequest}
        onDecline={() => setPendingMuteRequest(null)}
      />
    </div>
  )
}

const defaultUIKitContainerStyle = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  flexDirection: 'column',
  background: '#020617',
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
}

const defaultGridContainerStyle = {
  width: '100%',
  height: '100%',
  display: 'grid',
  gap: '8px',
  padding: '8px',
  boxSizing: 'border-box',
}

const defaultGridCellStyle = {
  width: '100%',
  height: '100%',
  minHeight: 0,
}

const defaultPinnedContainerStyle = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  gap: '8px',
  padding: '8px',
  boxSizing: 'border-box',
}

const defaultPinnedPrimaryStyle = {
  display: 'flex',
  flex: 5,
  minWidth: 0,
  minHeight: 0,
}

const defaultPinnedSecondaryStyle = {
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: '8px',
  overflowY: 'auto',
  minWidth: '240px',
}

const defaultPinnedSecondaryCellStyle = {
  minHeight: '180px',
  flexShrink: 0,
}

const defaultLocalButtonContainerStyle = {
  position: 'absolute',
  left: '50%',
  bottom: '24px',
  transform: 'translateX(-50%)',
  zIndex: 10000,
  display: 'flex',
  gap: '10px',
  justifyContent: 'center',
  alignItems: 'center',
}

const defaultLocalButtonStyle = {
  width: '48px',
  height: '48px',
  borderRadius: '14px',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  background: 'rgba(15, 23, 42, 0.72)',
  color: '#f8fafc',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 12px 32px rgba(15, 23, 42, 0.25)',
  backdropFilter: 'blur(16px)',
}

const endCallButtonStyle = {
  background:
    'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 1) 100%)',
  border: '1px solid rgba(255, 255, 255, 0.28)',
}

const deviceErrorOverlayStyle = {
  position: 'fixed',
  top: '18px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10050,
  width: 'min(540px, calc(100vw - 32px))',
  display: 'flex',
  justifyContent: 'center',
  // Only the card should capture clicks — the rest of the call stays interactive behind it.
  pointerEvents: 'none',
}

const deviceErrorCardStyle = {
  pointerEvents: 'auto',
  display: 'flex',
  gap: '14px',
  alignItems: 'flex-start',
  width: '100%',
  padding: '16px 18px',
  borderRadius: '16px',
  background: 'rgba(15, 23, 42, 0.94)',
  border: '1px solid rgba(248, 113, 113, 0.45)',
  boxShadow: '0 18px 48px rgba(2, 6, 23, 0.55)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  color: '#f8fafc',
}

const deviceErrorIconWrapStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  background: 'rgba(248, 113, 113, 0.14)',
  flexShrink: 0,
}

const deviceErrorTitleStyle = {
  fontSize: '15px',
  fontWeight: 700,
  lineHeight: 1.3,
  marginBottom: '4px',
}

const deviceErrorDetailStyle = {
  fontSize: '13px',
  lineHeight: 1.45,
  color: 'rgba(226, 232, 240, 0.92)',
  marginBottom: '8px',
}

const deviceErrorRemedyListStyle = {
  margin: '0 0 12px',
  padding: '0 0 0 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}

const deviceErrorRemedyItemStyle = {
  fontSize: '12.5px',
  lineHeight: 1.45,
  color: 'rgba(203, 213, 225, 0.95)',
}

const deviceErrorActionsStyle = {
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  flexWrap: 'wrap',
}

const deviceErrorPrimaryButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 14px',
  borderRadius: '10px',
  border: '1px solid rgba(148, 163, 184, 0.28)',
  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
  color: '#f8fafc',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
}

const deviceErrorSecondaryButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderRadius: '10px',
  border: '1px solid rgba(148, 163, 184, 0.28)',
  background: 'transparent',
  color: 'rgba(226, 232, 240, 0.9)',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
}

const deviceErrorCloseButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '8px',
  border: 'none',
  background: 'transparent',
  color: 'rgba(148, 163, 184, 0.9)',
  cursor: 'pointer',
  flexShrink: 0,
}

const defaultUsernameTextStyle = {
  position: 'absolute',
  top: '12px',
  left: '12px',
  margin: 0,
  padding: '8px 14px',
  borderRadius: '999px',
  maxWidth: 'calc(100% - 24px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: 600,
  zIndex: 2,
  background: 'rgba(15, 23, 42, 0.72)',
  backdropFilter: 'blur(14px)',
}

const defaultStatusRowStyle = {
  position: 'absolute',
  left: '12px',
  right: '12px',
  bottom: '12px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  alignItems: 'center',
  zIndex: 2,
  pointerEvents: 'none',
  padding: 0,
  background: 'transparent',
  boxShadow: 'none',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
}

const defaultStatusPillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  minHeight: '28px',
  padding: '6px 10px',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.01em',
  boxShadow: '0 10px 24px rgba(139, 92, 246, 0.12)',
  backdropFilter: 'blur(14px)',
}

const remoteControlButtonStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  border: '1px solid rgba(255, 255, 255, 0.16)',
  background: 'rgba(15, 23, 42, 0.78)',
  color: '#f8fafc',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const defaultDeviceSettingsTheme = {
  dialog: {
    width: 'min(360px, calc(100vw - 32px))',
    color: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
  },
  headerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '22px 22px 16px',
  },
  eyebrowStyle: {
    marginBottom: '6px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#7dd3fc',
  },
  titleStyle: {
    fontSize: '17px',
    fontWeight: 700,
    color: '#f8fafc',
    lineHeight: 1.25,
  },
  bodyStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '0 22px 22px',
    minHeight: 0,
  },
  summaryStyle: {
    color: 'rgba(226, 232, 240, 0.8)',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  groupStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '14px',
    borderRadius: '18px',
    background:
      'linear-gradient(180deg, rgba(15, 23, 42, 0.72) 0%, rgba(15, 23, 42, 0.88) 100%)',
    border: '1px solid rgba(148, 163, 184, 0.16)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.04)',
  },
  fieldHeaderStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#f8fafc',
  },
  fieldMetaStyle: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(148, 163, 184, 0.78)',
  },
  closeButtonStyle: {
    border: 0,
    background: 'rgba(30, 41, 59, 0.92)',
    color: '#e2e8f0',
    cursor: 'pointer',
    borderRadius: '999px',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    boxShadow: '0 10px 24px rgba(2, 6, 23, 0.22)',
  },
  paperSx: {
    mt: -1,
    overflow: 'visible',
    borderRadius: '26px',
    background:
      'radial-gradient(circle at top, rgba(14, 165, 233, 0.12), rgba(15, 23, 42, 0.96) 58%)',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    boxShadow: '0 28px 80px rgba(2, 6, 23, 0.48)',
    backdropFilter: 'blur(22px)',
    WebkitBackdropFilter: 'blur(22px)',
    color: '#f8fafc',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      bottom: '-9px',
      width: '18px',
      height: '18px',
      transform: 'translateX(-50%) rotate(45deg)',
      background: 'rgba(15, 23, 42, 0.96)',
      borderRight: '1px solid rgba(148, 163, 184, 0.18)',
      borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
    },
  },
  selectSx: {
    color: '#f8fafc',
    borderRadius: '14px',
    background: 'rgba(15, 23, 42, 0.72)',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(148, 163, 184, 0.22)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(125, 211, 252, 0.34)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#38bdf8',
      borderWidth: '1px',
    },
    '& .MuiSelect-icon': {
      color: '#cbd5f5',
    },
  },
  selectMenuProps: {
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: '16px',
        background: 'rgba(15, 23, 42, 0.98)',
        color: '#f8fafc',
        border: '1px solid rgba(148, 163, 184, 0.18)',
        boxShadow: '0 24px 60px rgba(2, 6, 23, 0.42)',
        '& .MuiMenuItem-root': {
          fontSize: '14px',
        },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: 'rgba(56, 189, 248, 0.16)',
        },
        '& .MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: 'rgba(56, 189, 248, 0.22)',
        },
      },
    },
  },
}

/** Frosted violet glass — aligned with kiosk chamber header / ModernChamberVideoInterface chrome */
const chamberKioskDeviceSettingsTheme = {
  dialog: {
    width: 'min(360px, calc(100vw - 32px))',
    color: '#4c1d95',
    display: 'flex',
    flexDirection: 'column',
  },
  headerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '22px 22px 16px',
  },
  eyebrowStyle: {
    marginBottom: '6px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#6d28d9',
  },
  titleStyle: {
    fontSize: '17px',
    fontWeight: 700,
    color: '#4c1d95',
    lineHeight: 1.25,
  },
  bodyStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '0 22px 22px',
    minHeight: 0,
  },
  summaryStyle: {
    color: 'rgba(76, 29, 149, 0.78)',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  groupStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '14px',
    borderRadius: '18px',
    background:
      'linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(250, 245, 255, 0.92) 100%)',
    border: '1px solid rgba(216, 180, 254, 0.65)',
    boxShadow:
      '0 10px 28px rgba(139, 92, 246, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.95)',
  },
  fieldHeaderStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#5b21b6',
  },
  fieldMetaStyle: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(91, 33, 182, 0.55)',
  },
  closeButtonStyle: {
    border: '1px solid rgba(216, 180, 254, 0.72)',
    background: 'rgba(255, 255, 255, 0.92)',
    color: '#5b21b6',
    cursor: 'pointer',
    borderRadius: '999px',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    boxShadow: '0 12px 26px rgba(139, 92, 246, 0.14)',
  },
  paperSx: {
    mt: -1,
    overflow: 'visible',
    borderRadius: '26px',
    background:
      'linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(245, 243, 255, 0.9) 100%)',
    border: '1px solid rgba(216, 180, 254, 0.72)',
    boxShadow:
      '0 22px 52px rgba(91, 33, 182, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.88)',
    backdropFilter: 'blur(22px) saturate(140%)',
    WebkitBackdropFilter: 'blur(22px) saturate(140%)',
    color: '#4c1d95',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      bottom: '-9px',
      width: '18px',
      height: '18px',
      transform: 'translateX(-50%) rotate(45deg)',
      background: 'rgba(245, 243, 255, 0.96)',
      borderRight: '1px solid rgba(216, 180, 254, 0.72)',
      borderBottom: '1px solid rgba(216, 180, 254, 0.72)',
    },
  },
  selectSx: {
    color: '#4c1d95',
    borderRadius: '14px',
    background: 'rgba(255, 255, 255, 0.95)',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(167, 139, 250, 0.5)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(139, 92, 246, 0.55)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7c3aed',
      borderWidth: '1px',
    },
    '& .MuiSelect-icon': {
      color: '#5b21b6',
    },
  },
  selectMenuProps: {
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: '16px',
        background: 'rgba(255, 255, 255, 0.98)',
        color: '#4c1d95',
        border: '1px solid rgba(216, 180, 254, 0.65)',
        boxShadow: '0 24px 56px rgba(91, 33, 182, 0.16)',
        '& .MuiMenuItem-root': {
          fontSize: '14px',
        },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: 'rgba(139, 92, 246, 0.14)',
        },
        '& .MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: 'rgba(139, 92, 246, 0.22)',
        },
      },
    },
  },
}

const remoteMuteDialogContainerStyle = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  paddingBottom: '112px',
  zIndex: 10003,
}

const remoteMuteDialogCardStyle = {
  pointerEvents: 'auto',
  width: 'min(360px, calc(100% - 32px))',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px',
  borderRadius: '18px',
  background: 'rgba(15, 23, 42, 0.92)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  color: '#f8fafc',
  boxShadow: '0 24px 80px rgba(2, 6, 23, 0.48)',
}

const remoteMutePrimaryButtonStyle = {
  border: 0,
  borderRadius: '12px',
  padding: '10px 14px',
  background: '#4f46e5',
  color: '#ffffff',
  cursor: 'pointer',
}

const remoteMuteSecondaryButtonStyle = {
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '12px',
  padding: '10px 14px',
  background: 'transparent',
  color: '#e2e8f0',
  cursor: 'pointer',
}
